import { useEffect, useState, useCallback, useRef } from "react";
import { PolicySaysChip } from "../components/PolicySaysChip";
import { useSearchParams } from "react-router-dom";
import { Building2, Plus, ChevronRight, X, AlertTriangle, CheckCircle, Clock, GitBranch, Send, Copy, Lock, Boxes, FileText } from "lucide-react";
import { theme, card, floatingSheet, emptyFrame, btnPrimary, btnSecondary, btnDanger, inputStyle, pageHeading, type as text } from "../theme";
import { fmtDate } from "../lib/format";
import { fmtDateOnly } from "../lib/dateOnly";
import { gbpMo, TPRM_ADDON_PENCE } from "../lib/pricing";
import { api, apiErrorMessage, ApiError } from "../api/client";
import {
  CONTRACT_KIND_LABELS, CONTRACT_STANDING_LABELS,
  contractTiming, contractInForce, termsRequiredFor, termCoverage, combinedTermCoverage, withLegacyTerms,
  type ContractKind, type ContractStanding, type ContractRelationship,
} from "../lib/supplierContracts";
import { PortalLink } from "../components/PortalLink";
import { IndexTabs, RulerProgress } from "../components/Drawing";
import { safeHref } from "../utils/safeHref";
import {
  useQuestionnaireCatalogue, findQuestionSet, QuestionnaireTemplatesTab,
  type QuestionnaireCatalogue, type TprmQuestion,
} from "../components/TprmQuestionnaireTemplates";
import { SupplierAssetsPanel, deliversLabel, dependentAssetsNotice, type SupplierAsset, type DependentAssets } from "../components/SupplierAssetsPanel";
import { EntityTagsRow, EntityTagFilters, useEntityTagData, useTagFilter, matchesEntityTagFilter, entityRef, KIND_HREFS } from "../components/EntityTags";
import { RelatedRecords, useLinkedActions, actionLinks, riskLabel, type RelatedLink } from "../components/RelatedRecords";
import { NextSteps, type NextStepItem } from "../components/NextSteps";
import { RegisterToolbar, SummaryTiles } from "../components/RegisterToolbar";
import { downloadCsv, matchesSearch, by, type SortOption } from "../lib/registerExport";
import { useCurrentUser } from "../auth/useCurrentUser";
import { DetailFields, DetailSection, EditButton, RecordActions } from "../components/RecordView";
import {
  ContractsCalendar, ConcentrationCard, ConcentrationLine, QuestionnaireChase, contractCalendar, noticeDeadlinesWithin,
  type SupplierConcentrationRow, type OutstandingQuestionnaire, type AutoChaseSummary,
} from "../components/TprmWorkViews";
import { useNewFromLink } from "../hooks/useNewFromLink";
import { SupplierAssurancePanel, StandingChip, STANDING_LABELS, type SupplierStanding } from "../components/SupplierAssurancePanel";
import { SupplierCompromisePanel } from "../components/SupplierCompromisePanel";
import { SupplierAutomationCard, SharedFourthPartiesCard, SupplierGuestAccessCard, VendorRatingsCard } from "../components/SupplierMonitoringCards";
import { SupplierRenewalPanel } from "../components/SupplierRenewalPanel";
import { ContractReadingPanel } from "../components/ContractReadingPanel";
import { SupplierContractForm, emptyContract, toContractForm, type ContractForm } from "../components/SupplierContractForm";
import { PlaybookBar } from "../components/PlaybookBar";
import { SeverityPill, StatusPill, type SupplierNoticeVerdict } from "./resilience/shared";

// ── Types ─────────────────────────────────────────────────────────────────────

interface QAnswer {
  question:       string;
  answer:         string;
  riskFlag?:      boolean;
  adverseAnswer?: "yes" | "no";
  guidance?:      string;
  // A question answered with a list of the supplier's subcontractors rather than Yes / No.
  answerType?:    "subcontractors";
  parties?:       Array<{ name: string; service?: string | null; country?: string | null }>;
}

interface Questionnaire {
  id:              string;
  title:           string;
  status:          string;
  riskScore:       number | null;
  dataConfidence:  number | null;
  createdAt:       string;
  dueDate:         string | null;
  returnedAt:      string | null;
  answers:         QAnswer[] | null;
  respondentName:  string | null;
  respondentEmail: string | null;
}

interface SendResult {
  portalUrl: string;
  expiresAt: string;
  emailedTo: string | null;
}

interface ScopeChange {
  id:          string;
  changeType:  string;
  description: string;
  changedAt:   string;
}

interface MonitoringActivity {
  lastDone: string | null;
  nextDue:  string | null;
  notes:    string;
}

/** One signed agreement (backend/src/lib/supplierContracts.ts). */
interface Contract {
  id:               string;
  supplierId:       string;
  title:            string;
  reference:        string | null;
  kind:             string;
  status:           string;
  startDate:        string | null;
  endDate:          string | null;
  autoRenew:        boolean;
  noticePeriodDays: number | null;
  reviewDate:       string | null;
  owner:            string | null;
  annualValue:      number | null;
  currency:         string | null;
  // Only the terms the catalogue knows are ever stored; an unanswered term is absent.
  terms:            Record<string, boolean> | null;
  incidentNotificationHours: number | null;
  /** Restore commitment, three-state: null = unanswered, false = none, true = restoreTimeHours (lib/supplierContracts.ts). */
  restoreCommitted?: boolean | null;
  restoreTimeHours?: number | null;
  restorePointHours?: number | null;
  dataLocations:    string[];
  documentUrl:      string | null;
  notes:            string | null;
}

interface Supplier {
  id:              string;
  name:            string;
  category:        string;
  website:         string | null;
  contactName:     string | null;
  contactEmail:    string | null;
  internalOwner:   string | null;
  /** The member who owns the relationship (lib/ownership.ts): chosen, or matched exactly from internalOwner. */
  internalOwnerUserId?: string | null;
  riskTier:        string;
  riskBasis:       string | null;
  status:          string;
  notes:           string | null;
  overallScore:    number | null;
  lastReviewDate:  string | null;
  nextReviewDate:  string | null;
  monitoringState: Record<string, MonitoringActivity> | null;
  questionnaires:  Questionnaire[];
  scopeChanges:    ScopeChange[];
  isDataProcessor: boolean;
  /** A RoPA activity names it as processor (the Art. 28 terms apply, flagged or not). */
  ropaProcessor?: boolean;
  certifications?: SupplierCertification[];
  approvedAt?:     string | null;
  approvalNote?:   string | null;
  // The asset register rows delivered by this supplier (Asset.supplierId).
  assets?:         SupplierAsset[];
  // The agreements behind the relationship (lib/supplierContracts.ts).
  contracts?:      Contract[];
  // The register risk lib/tprmLinkedRisk.ts keeps in step with the supplier's score.
  tprmLinkedRisk?: { id: string; title: string; displayId: number | null; status: string; residualScore: number | null } | null;
}

// An incident the supplier caused or that came through it (GET /tprm/suppliers/:id), with whether
// it told the organisation inside its agreement's window and whether a review is due since.
interface SupplierIncident {
  id:                 string;
  displayId:          number | null;
  ref:                string;
  title:              string;
  severity:           string;
  status:             string;
  occurredAt:         string | null;
  detectedAt:         string;
  supplierNotifiedAt: string | null;
  notice:             SupplierNoticeVerdict;
  reviewDue:          boolean;
}

// A certification or attestation the supplier holds (operational resilience module).
interface SupplierCertification {
  id:             string;
  standard:       string;
  certificateRef: string | null;
  issuer:         string | null;
  scope:          string | null;
  issuedAt:       string | null;
  expiresAt:      string | null;
  url:            string | null;
  verifiedAt:     string | null;
  notes:          string | null;
}

const CERTIFICATION_STANDARDS: Array<{ id: string; label: string }> = [
  { id: "iso27001", label: "ISO/IEC 27001" },
  { id: "iso22301", label: "ISO 22301 (business continuity)" },
  { id: "iso9001", label: "ISO 9001" },
  { id: "iso27701", label: "ISO/IEC 27701 (privacy)" },
  { id: "soc2_type1", label: "SOC 2 Type I" },
  { id: "soc2_type2", label: "SOC 2 Type II" },
  { id: "cyber_essentials", label: "Cyber Essentials" },
  { id: "cyber_essentials_plus", label: "Cyber Essentials Plus" },
  { id: "iasme_ca", label: "IASME Cyber Assurance" },
  { id: "pci_dss", label: "PCI DSS" },
  { id: "csa_star", label: "CSA STAR" },
  { id: "nist_csf_attestation", label: "NIST CSF attestation" },
  { id: "other", label: "Other" },
];
const certificationLabel = (id: string) => CERTIFICATION_STANDARDS.find((c) => c.id === id)?.label ?? id;

type Tab = "suppliers" | "contracts" | "questionnaires" | "monitoring";
type PanelTab = "overview" | "assessments" | "assurance" | "compromise" | "contracts" | "scope" | "monitoring" | "certifications" | "assets";

// ── Constants ─────────────────────────────────────────────────────────────────

const TIER_COLORS: Record<string, { bg: string; text: string }> = {
  critical: { bg: "var(--c-dangerBg)",  text: "var(--c-dangerText)" },
  high:     { bg: "var(--c-dangerBg)",  text: "var(--c-dangerText)" },
  medium:   { bg: "var(--c-warningBg)", text: "var(--c-warningText)" },
  low:      { bg: "var(--c-successBg)", text: "var(--c-successText)" },
};

const STATUS_COLORS: Record<string, { bg: string; text: string }> = {
  prospective:  { bg: "var(--c-accentBg)",  text: "var(--c-accentText)" },
  active:       { bg: "var(--c-successBg)", text: "var(--c-successText)" },
  under_review: { bg: "var(--c-warningBg)", text: "var(--c-warningText)" },
  inactive:     { bg: "var(--c-faint)",     text: "var(--c-muted)" },
  offboarded:   { bg: "var(--c-faint)",     text: "var(--c-muted)" },
  rejected:     { bg: "var(--c-dangerBg)",  text: "var(--c-dangerText)" },
};

const Q_STATUS_COLORS: Record<string, { bg: string; text: string }> = {
  draft:    { bg: "var(--c-faint)",     text: "var(--c-muted)" },
  sent:     { bg: "var(--c-accentBg)",  text: "var(--c-accentText)" },
  returned: { bg: "var(--c-warningBg)", text: "var(--c-warningText)" },
  reviewed: { bg: "var(--c-successBg)", text: "var(--c-successText)" },
  expired:  { bg: "var(--c-dangerBg)",  text: "var(--c-dangerText)" },
};

const TIER_LABELS: Record<string, string> = { low: "Low", medium: "Medium", high: "High", critical: "Critical" };
const STATUS_LABELS: Record<string, string> = {
  prospective: "Prospective", active: "Active", inactive: "Inactive",
  under_review: "Under review", offboarded: "Offboarded", rejected: "Rejected",
};
const tierLabel   = (t: string) => TIER_LABELS[t] ?? t;
const statusLabel = (s: string) => STATUS_LABELS[s] ?? s.replace(/_/g, " ");
// A new supplier is a relationship being procured or one already in use (backend lib/procurementGate.ts).
const CREATABLE_STATUSES = ["prospective", "active", "under_review", "inactive"];

const CATEGORIES = ["Cloud", "Software", "Professional Services", "Data Processing", "Hardware", "Other"];
const RISK_TIERS = ["low", "medium", "high", "critical"];
// `prospective` is the procurement gate: it becomes `active` only through the approval step in the panel.
const STATUSES   = ["prospective", "active", "inactive", "under_review", "offboarded", "rejected"];

const SCOPE_CHANGE_TYPES: Record<string, string> = {
  service_expansion: "Service expansion",
  data_access:       "New data access",
  geography:         "Geography change",
  personnel:         "Key personnel change",
  contract:          "Contract modification",
  other:             "Other",
};

// Gartner Insight 3 — monitoring strategies from Figure 5
const MONITORING_ACTIVITIES: Array<{ key: string; label: string; typical: string }> = [
  { key: "contract_review",   label: "Contract performance review",           typical: "6 months" },
  { key: "self_assessment",   label: "Third-party self-assessment / reports",  typical: "12 months" },
  { key: "iso_soc_check",     label: "ISO / SOC audit review",                 typical: "12 months" },
  { key: "financial_review",  label: "Financial condition review",             typical: "12 months" },
  { key: "licensing_check",   label: "Licensing and registration check",       typical: "12 months" },
  { key: "internal_audit",    label: "Internal audit results review",          typical: "12 months" },
  { key: "open_source",       label: "Open-source intelligence research",      typical: "6 months" },
  { key: "on_site_audit",     label: "On-site audit / site visit",             typical: "24 months" },
];

// ── Helpers ───────────────────────────────────────────────────────────────────

/** "under_review" -> "Under review". */
function sentenceCase(v: string): string {
  const words = v.replace(/_/g, " ");
  return words.charAt(0).toUpperCase() + words.slice(1);
}

function Pill({ label, colors }: { label: string; colors: { bg: string; text: string } }) {
  return (
    <span style={{
      display: "inline-block", padding: "2px 10px", borderRadius: 3,
      fontSize: 11, fontWeight: 600, background: colors.bg, color: colors.text, whiteSpace: "nowrap",
    }}>
      {sentenceCase(label)}
    </span>
  );
}

/**
 * The one line the register row carries about agreements: what is in force and
 * whatever needs a decision first. Nothing in force on a supplier in use is
 * itself worth saying — it is the question an assessor asks first.
 */
function contractLabel(s: Supplier): { text: string; warn: boolean } | null {
  const contracts = s.contracts ?? [];
  const ended = s.status === "inactive" || s.status === "offboarded" || s.status === "rejected";
  if (contracts.length === 0) {
    if (ended || s.status === "prospective") return null;
    return { text: "no agreement on file", warn: true };
  }
  const standings = contracts.map((c) => contractTiming(c).standing);
  const live = standings.filter(contractInForce).length;
  const needsDecision = standings.filter((x) => x === "notice_due").length;
  const expiring = standings.filter((x) => x === "expiring").length;
  if (live === 0) return { text: ended ? `${contracts.length} agreement${contracts.length === 1 ? "" : "s"}, none in force` : "no agreement in force", warn: !ended };
  const text = `${live} agreement${live === 1 ? "" : "s"}${needsDecision ? ` · ${needsDecision} renewal decision due` : expiring ? ` · ${expiring} expiring` : ""}`;
  return { text, warn: needsDecision > 0 || expiring > 0 };
}

function isOverdue(iso: string | null | undefined) {
  if (!iso) return false;
  return new Date(iso) <= new Date();
}

// Gartner Insight 2: Confidence label + colour
function ConfidenceBadge({ level }: { level: number | null }) {
  if (!level) return null;
  const labels = ["", "Very low", "Low", "Moderate", "High", "Very high"];
  const colours = ["", "var(--c-dangerText)", "var(--c-warningText)", "var(--c-muted)", "var(--c-successText)", "var(--c-successText)"];
  return (
    <span style={{ fontSize: 11, color: colours[level], fontWeight: 600 }}>
      Data confidence: {labels[level]} ({level}/5)
    </span>
  );
}

// ── Supplier form ─────────────────────────────────────────────────────────────

interface SupplierFormProps {
  initial?: Partial<Supplier>;
  onSave:   (data: Partial<Supplier>) => Promise<void>;
  onCancel: () => void;
  saving:   boolean;
}

function SupplierForm({ initial, onSave, onCancel, saving }: SupplierFormProps) {
  const [ownerUserId, setOwnerUserId] = useState<string>(initial?.internalOwnerUserId ?? "");
  const [members, setMembers] = useState<Array<{ id: string; name: string }>>([]);
  useEffect(() => {
    api.get<{ members: Array<{ id: string; displayName: string | null; email: string }> }>("/organizations/members")
      .then((d) => setMembers(d.members.map((m) => ({ id: m.id, name: m.displayName || m.email })))).catch(() => {});
  }, []);
  const [form, setForm] = useState({
    name:          initial?.name          ?? "",
    category:      initial?.category      ?? "Software",
    website:       initial?.website       ?? "",
    contactName:   initial?.contactName   ?? "",
    contactEmail:  initial?.contactEmail  ?? "",
    internalOwner: initial?.internalOwner ?? "",
    riskTier:      initial?.riskTier      ?? "medium",
    riskBasis:     initial?.riskBasis     ?? "",
    status:        initial?.status        ?? "active",
    notes:         initial?.notes         ?? "",
    lastReviewDate: initial?.lastReviewDate?.slice(0, 10) ?? "",
    nextReviewDate: initial?.nextReviewDate?.slice(0, 10) ?? "",
  });

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    await onSave({
      // An emptied field is sent as "" (which the server stores as null); undefined would keep the old value.
      ...form,
      website:       form.website.trim(),
      contactName:   form.contactName.trim(),
      contactEmail:  form.contactEmail.trim(),
      internalOwner: form.internalOwner.trim(),
      // Sent only when a member is chosen or cleared here; otherwise the server matches the text exactly.
      ...(ownerUserId !== (initial?.internalOwnerUserId ?? "") ? { internalOwnerUserId: ownerUserId || null } : {}),
      riskBasis:     form.riskBasis.trim(),
      lastReviewDate: form.lastReviewDate ? new Date(form.lastReviewDate).toISOString() : null,
      nextReviewDate: form.nextReviewDate ? new Date(form.nextReviewDate).toISOString() : null,
    });
  }

  return (
    <form onSubmit={handleSubmit} style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
      <label style={{ display: "flex", flexDirection: "column", gap: 5, fontSize: 13, gridColumn: "1 / -1" }}>
        <span style={{ fontWeight: 500, color: theme.color.text }}>Supplier name *</span>
        <input
          type="text" value={form.name} required
          onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
          placeholder="Acme Cloud Ltd"
          style={{ ...inputStyle, fontSize: 13 }}
        />
      </label>

      <label style={{ display: "flex", flexDirection: "column", gap: 5, fontSize: 13 }}>
        <span style={{ fontWeight: 500, color: theme.color.text }}>Category</span>
        <select value={form.category} onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))} style={{ ...inputStyle, fontSize: 13 }}>
          {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      </label>

      <label style={{ display: "flex", flexDirection: "column", gap: 5, fontSize: 13 }}>
        <span style={{ fontWeight: 500, color: theme.color.text }}>Risk tier</span>
        <select value={form.riskTier} onChange={(e) => setForm((f) => ({ ...f, riskTier: e.target.value }))} style={{ ...inputStyle, fontSize: 13 }}>
          {RISK_TIERS.map((t) => <option key={t} value={t}>{tierLabel(t)}</option>)}
        </select>
      </label>

      {/* Gartner Insight 1: Internal owner — accountability */}
      <label style={{ display: "flex", flexDirection: "column", gap: 5, fontSize: 13 }}>
        <span style={{ fontWeight: 500, color: theme.color.text }}>Internal owner</span>
        <input
          type="text" value={form.internalOwner}
          onChange={(e) => setForm((f) => ({ ...f, internalOwner: e.target.value }))}
          placeholder="e.g. IT Manager / Procurement"
          style={{ ...inputStyle, fontSize: 13 }}
        />
      </label>

      <label style={{ display: "flex", flexDirection: "column", gap: 5, fontSize: 13 }}>
        <span style={{ fontWeight: 500, color: theme.color.text }}>Owner is a member</span>
        <select
          value={ownerUserId}
          onChange={(e) => {
            const id = e.target.value;
            setOwnerUserId(id);
            const name = members.find((m) => m.id === id)?.name;
            if (id && name && !form.internalOwner.trim()) setForm((f) => ({ ...f, internalOwner: name }));
          }}
          style={{ ...inputStyle, fontSize: 13 }}
        >
          <option value="">Not linked to a member</option>
          {ownerUserId && !members.some((m) => m.id === ownerUserId) && <option value={ownerUserId}>Current owner (no longer active)</option>}
          {members.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
        </select>
      </label>

      <label style={{ display: "flex", flexDirection: "column", gap: 5, fontSize: 13 }}>
        <span style={{ fontWeight: 500, color: theme.color.text }}>Website</span>
        <input type="url" value={form.website} onChange={(e) => setForm((f) => ({ ...f, website: e.target.value }))} placeholder="https://example.com" style={{ ...inputStyle, fontSize: 13 }} />
      </label>

      <label style={{ display: "flex", flexDirection: "column", gap: 5, fontSize: 13 }}>
        <span style={{ fontWeight: 500, color: theme.color.text }}>Contact name</span>
        <input type="text" value={form.contactName} onChange={(e) => setForm((f) => ({ ...f, contactName: e.target.value }))} placeholder="Jane Smith" style={{ ...inputStyle, fontSize: 13 }} />
      </label>

      <label style={{ display: "flex", flexDirection: "column", gap: 5, fontSize: 13 }}>
        <span style={{ fontWeight: 500, color: theme.color.text }}>Contact email</span>
        <input type="email" value={form.contactEmail} onChange={(e) => setForm((f) => ({ ...f, contactEmail: e.target.value }))} placeholder="jane@supplier.com" style={{ ...inputStyle, fontSize: 13 }} />
      </label>

      <label style={{ display: "flex", flexDirection: "column", gap: 5, fontSize: 13 }}>
        <span style={{ fontWeight: 500, color: theme.color.text }}>Status</span>
        <select
          value={form.status}
          onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
          disabled={initial?.status === "prospective"}
          style={{ ...inputStyle, fontSize: 13 }}
        >
          {(initial ? STATUSES : CREATABLE_STATUSES).map((s) => <option key={s} value={s}>{statusLabel(s)}</option>)}
        </select>
        {initial?.status === "prospective" && (
          <span style={{ ...text.hint }}>Approve or reject a prospective supplier from its panel.</span>
        )}
      </label>

      <label style={{ display: "flex", flexDirection: "column", gap: 5, fontSize: 13 }}>
        <span style={{ fontWeight: 500, color: theme.color.text }}>Last review</span>
        <input type="date" value={form.lastReviewDate} onChange={(e) => setForm((f) => ({ ...f, lastReviewDate: e.target.value }))} style={{ ...inputStyle, fontSize: 13 }} />
      </label>

      <label style={{ display: "flex", flexDirection: "column", gap: 5, fontSize: 13 }}>
        <span style={{ fontWeight: 500, color: theme.color.text }}>Next review</span>
        <input type="date" value={form.nextReviewDate} onChange={(e) => setForm((f) => ({ ...f, nextReviewDate: e.target.value }))} style={{ ...inputStyle, fontSize: 13 }} />
      </label>

      {/* Gartner Insight 1: Risk basis — why was this tier assigned? */}
      <label style={{ display: "flex", flexDirection: "column", gap: 5, fontSize: 13, gridColumn: "1 / -1" }}>
        <span style={{ fontWeight: 500, color: theme.color.text }}>Risk tier rationale</span>
        <textarea
          value={form.riskBasis}
          onChange={(e) => setForm((f) => ({ ...f, riskBasis: e.target.value }))}
          placeholder="Why was this risk tier assigned? e.g. 'Processes payment card data, has access to production systems'"
          rows={2}
          style={{ ...inputStyle, fontSize: 13, resize: "vertical" }}
        />
      </label>

      <label style={{ display: "flex", flexDirection: "column", gap: 5, fontSize: 13, gridColumn: "1 / -1" }}>
        <span style={{ fontWeight: 500, color: theme.color.text }}>Notes</span>
        <textarea
          value={form.notes}
          onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
          placeholder="Any additional context about this supplier relationship…"
          rows={2}
          style={{ ...inputStyle, fontSize: 13, resize: "vertical" }}
        />
      </label>

      <div style={{ gridColumn: "1 / -1", display: "flex", gap: 10, marginTop: 4 }}>
        <button type="submit" disabled={saving}
          style={{ ...btnPrimary, padding: "9px 20px", fontSize: 13, cursor: saving ? "not-allowed" : "pointer", opacity: saving ? 0.6 : 1 }}>
          {saving ? "Saving…" : (initial ? "Save changes" : "Add supplier")}
        </button>
        <button type="button" onClick={onCancel}
          style={{ padding: "9px 16px", fontSize: 13, borderRadius: 3, border: "1px solid var(--c-border)", background: "var(--c-panel)", color: theme.color.muted, cursor: "pointer" }}>
          Cancel
        </button>
      </div>
    </form>
  );
}

// ── QAnswers ──────────────────────────────────────────────────────────────────

function QAnswers({ answers }: { answers: QAnswer[] | null }) {
  if (!answers || answers.length === 0) {
    return <div style={{ fontSize: 12, color: theme.color.muted }}>No answers recorded.</div>;
  }
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {answers.map((a, i) => a.answerType === "subcontractors" ? (
        <div key={i} style={{ fontSize: 12 }}>
          <div style={{ color: theme.color.text, lineHeight: 1.4 }}>{a.question}</div>
          <div style={{ color: theme.color.muted, marginTop: 2, lineHeight: 1.5 }}>
            {(a.parties ?? []).length === 0
              ? "None listed."
              : (a.parties ?? []).map((p) => [p.name, p.service, p.country].filter(Boolean).join(", ")).join("; ")}
          </div>
        </div>
      ) : (
        <div key={i} style={{ display: "flex", justifyContent: "space-between", gap: 12, fontSize: 12 }}>
          <span style={{ color: theme.color.text, flex: 1, lineHeight: 1.4 }}>{a.question}</span>
          <span style={{
            fontWeight: 600, whiteSpace: "nowrap",
            color: a.riskFlag ? "var(--c-dangerText)" : a.answer && a.answer !== "N/A" ? "var(--c-successText)" : theme.color.muted,
          }}>
            {a.answer || "—"}
          </span>
        </div>
      ))}
    </div>
  );
}

// ── Supplier Panel (Assessments + Scope Changes + Monitoring) ─────────────────

interface SupplierPanelProps {
  supplier:  Supplier;
  catalogue: QuestionnaireCatalogue;
  initialTab?: PanelTab;
  onClose:   () => void;
  onUpdate:  () => void;
  onManageQuestionnaires: () => void;
  /** GET /tprm/concentration row for this supplier (the Overview tab's Concentration line). */
  concentration?: SupplierConcentrationRow;
  /** tprm:write: without it the panel is the read-only view alone (no Edit, no Actions). */
  canWrite: boolean;
  /** Open this questionnaire's view on the Assessments tab (the chase and the monitoring lists link here). */
  initialQuestionnaireId?: string | null;
  /** Saves the supplier's details; resolves to an error message, or null when saved. */
  onSaveSupplier: (data: Partial<Supplier>) => Promise<string | null>;
  savingSupplier: boolean;
  onDelete: () => void;
  onToggleDataProcessor: () => void;
  togglingDataProcessor: boolean;
}

function BackLink({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button type="button" onClick={onClick} style={{ ...linkBtn, ...text.hint, color: "var(--c-accentText)", display: "inline-flex", alignItems: "center", gap: 4, marginBottom: 12 }}>
      <ChevronRight size={13} style={{ transform: "rotate(180deg)" }} /> {label}
    </button>
  );
}

/** The row above a tab's content that carries its one Edit button. */
function TabHead({ title, children }: { title?: string; children?: React.ReactNode }) {
  if (!title && !children) return null;
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 12 }}>
      <div style={{ ...text.sectionTitle, margin: 0 }}>{title}</div>
      {children}
    </div>
  );
}

const rowBtn: React.CSSProperties = {
  ...card, display: "block", width: "100%", textAlign: "left", cursor: "pointer", font: "inherit",
  padding: "12px 14px",
};

function SupplierPanel({ supplier, catalogue, initialTab, onClose, onUpdate, onManageQuestionnaires, concentration, canWrite, initialQuestionnaireId, onSaveSupplier, savingSupplier, onDelete, onToggleDataProcessor, togglingDataProcessor }: SupplierPanelProps) {
  const [panelTab,       setPanelTab]       = useState<PanelTab>(initialTab ?? "overview");
  const [questionnaires, setQuestionnaires] = useState<Questionnaire[]>([]);
  const [scopeChanges,   setScopeChanges]   = useState<ScopeChange[]>([]);
  const [incidents,      setIncidents]      = useState<SupplierIncident[] | null>(null);
  const [loading,        setLoading]        = useState(true);
  const [creating,       setCreating]       = useState(false);
  const [saving,         setSaving]         = useState(false);
  // The questionnaire whose read-only view is open on the Assessments tab.
  const [activeQ,        setActiveQ]        = useState<string | null>(initialQuestionnaireId ?? null);
  const [answers,        setAnswers]        = useState<Record<string, string>>({});
  // Overview: the supplier's details form, behind Edit.
  const [editingDetails, setEditingDetails] = useState(false);
  const [detailsError,   setDetailsError]   = useState<string | null>(null);
  // Monitoring dates: read-only until Edit.
  const [editingMon,     setEditingMon]     = useState(false);
  // Reminder for a questionnaire sent and not returned (POST /tprm/questionnaires/:id/remind).
  const [reminding,      setReminding]      = useState(false);
  const [remindMsg,      setRemindMsg]      = useState<{ id: string; ok: boolean; text: string } | null>(null);
  // Why the last load or write on this panel failed, from the API's own message.
  const [actionError,    setActionError]    = useState<string | null>(null);

  function switchTab(t: PanelTab) {
    setPanelTab(t);
    setActionError(null);
    setEditingDetails(false);
    setEditingMon(false);
  }

  // Data confidence state for review modal
  const [reviewingQId,    setReviewingQId]    = useState<string | null>(null);
  const [reviewConfidence, setReviewConfidence] = useState<number>(3);
  // A ref as well as state: a double click fires twice before React re-renders the disabled button (DOC-29).
  const reviewBusyRef = useRef(false);
  const [reviewBusy, setReviewBusy] = useState(false);

  // Scope change form
  const [addingScope,      setAddingScope]      = useState(false);
  const [scopeForm,        setScopeForm]        = useState({ changeType: "service_expansion", description: "" });
  const [scopeSaving,      setScopeSaving]      = useState(false);

  // Monitoring state
  const [monState,         setMonState]         = useState<Record<string, MonitoringActivity>>(supplier.monitoringState ?? {});
  const [monSaving,        setMonSaving]        = useState(false);
  const [monSaved,         setMonSaved]         = useState(false);

  // Supplier portal send state
  const [sending,          setSending]          = useState(false);
  const [sendResult,       setSendResult]       = useState<SendResult | null>(null);
  const [linkCopied,       setLinkCopied]       = useState(false);

  // Which question set the next assessment uses: the built-in set for the
  // supplier's tier unless the assessor picks another, or one of the
  // organisation's own questionnaires.
  const defaultSetKey = `builtin:${supplier.riskTier}`;
  const [setKey, setSetKey] = useState(defaultSetKey);
  // When the next assessment is due back (optional; shown on the list and the reminder queue).
  const [dueDate, setDueDate] = useState("");
  const dueChoice = dueDate ? { dueDate: new Date(dueDate).toISOString() } : {};
  const selectedSet = findQuestionSet(catalogue, setKey)
    ?? findQuestionSet(catalogue, defaultSetKey)
    ?? findQuestionSet(catalogue, "builtin:medium");
  const questions: TprmQuestion[] = selectedSet?.questions ?? [];
  const questionnaireTitle = selectedSet?.templateId
    ? selectedSet.name
    : `Third-Party Risk Assessment (${selectedSet?.builtinTier ?? supplier.riskTier} tier)`;
  const setChoice = selectedSet?.templateId
    ? { templateId: selectedSet.templateId }
    : selectedSet?.builtinTier ? { builtinTier: selectedSet.builtinTier } : {};

  const loadData = useCallback(async () => {
    try {
      const [qs, sc] = await Promise.all([
        api.get<{ questionnaires: Questionnaire[] }>(`/tprm/suppliers/${supplier.id}/questionnaires`),
        api.get<{ scopeChanges: ScopeChange[] }>(`/tprm/suppliers/${supplier.id}/scope-changes`),
      ]);
      setQuestionnaires(qs.questionnaires);
      setScopeChanges(sc.scopeChanges);
    } catch (e) {
      setActionError(apiErrorMessage(e, "Could not load this supplier's assessments and scope changes"));
    }
    finally { setLoading(false); }
  }, [supplier.id]);

  useEffect(() => { loadData(); }, [loadData]);

  // The incidents are only on the single-supplier read; read again when a review is recorded, since
  // that settles "review due". Without them the Overview simply omits the section.
  useEffect(() => {
    let live = true;
    api.get<{ supplier: { incidents?: SupplierIncident[] } }>(`/tprm/suppliers/${supplier.id}`)
      .then((d) => { if (live) setIncidents(d.supplier.incidents ?? null); })
      .catch(() => { if (live) setIncidents(null); });
    return () => { live = false; };
  }, [supplier.id, supplier.lastReviewDate]);

  async function handleCreateQuestionnaire() {
    setSaving(true);
    setActionError(null);
    try {
      // Scoring, risk flags and draft-vs-returned are decided server-side from these answers.
      const answerArray = questions.map((q) => ({
        question:      q.text,
        answer:        answers[q.text] ?? "",
        adverseAnswer: q.adverseAnswer,
        ...(q.guidance ? { guidance: q.guidance } : {}),
      }));

      await api.post(`/tprm/suppliers/${supplier.id}/questionnaires`, {
        title:   questionnaireTitle,
        answers: answerArray,
        ...setChoice,
        ...dueChoice,
      });
      setCreating(false);
      setAnswers({});
      setDueDate("");
      await loadData();
      onUpdate();
    } catch (e) {
      setActionError(apiErrorMessage(e, "Could not save the assessment"));
    }
    finally { setSaving(false); }
  }

  // Create a draft with no answers and immediately issue a portal link so the
  // supplier answers it themselves (as opposed to the internal assessment flow).
  async function handleSendToSupplier() {
    setSending(true);
    setActionError(null);
    try {
      const { questionnaire } = await api.post<{ questionnaire: { id: string } }>(
        `/tprm/suppliers/${supplier.id}/questionnaires`,
        { title: questionnaireTitle, status: "draft", ...setChoice, ...dueChoice },
      );
      const result = await api.post<SendResult>(`/tprm/questionnaires/${questionnaire.id}/send`, {});
      setSendResult(result);
      setDueDate("");
      await loadData();
      onUpdate();
    } catch (e) {
      setActionError(apiErrorMessage(e, "Could not send the questionnaire to the supplier"));
    }
    finally { setSending(false); }
  }

  async function handleResend(qId: string) {
    setSending(true);
    setActionError(null);
    try {
      const result = await api.post<SendResult>(`/tprm/questionnaires/${qId}/send`, {});
      setSendResult(result);
      await loadData();
      onUpdate();
    } catch (e) {
      setActionError(apiErrorMessage(e, "Could not resend the questionnaire"));
    }
    finally { setSending(false); }
  }

  // Only a draft is offered for deletion: once sent or answered, the questionnaire is the record of
  // what the supplier was asked and said. The server audits the delete either way.
  async function handleDeleteDraft(q: Questionnaire) {
    if (!window.confirm(`Delete the draft "${q.title}"? Nothing has been sent to the supplier.`)) return;
    setActionError(null);
    try {
      await api.del(`/tprm/questionnaires/${q.id}`);
      setActiveQ(null);
      await loadData();
      onUpdate();
    } catch (e) {
      setActionError(apiErrorMessage(e, "Could not delete the draft"));
    }
  }

  async function handleReviewWithConfidence(qId: string, confidence: number) {
    if (reviewBusyRef.current) return;
    reviewBusyRef.current = true;
    setReviewBusy(true);
    setActionError(null);
    try {
      await api.patch(`/tprm/questionnaires/${qId}`, {
        status:         "reviewed",
        reviewedAt:     new Date().toISOString(),
        dataConfidence: confidence,
      });
      setReviewingQId(null);
      await loadData();
      onUpdate();
    } catch (e) {
      setActionError(apiErrorMessage(e, "Could not mark the assessment reviewed"));
    } finally {
      reviewBusyRef.current = false;
      setReviewBusy(false);
    }
  }

  async function addScopeChange() {
    if (!scopeForm.description.trim()) return;
    setScopeSaving(true);
    setActionError(null);
    try {
      await api.post(`/tprm/suppliers/${supplier.id}/scope-changes`, {
        changeType:  scopeForm.changeType,
        description: scopeForm.description,
      });
      setAddingScope(false);
      setScopeForm({ changeType: "service_expansion", description: "" });
      await loadData();
      onUpdate();
    } catch (e) {
      setActionError(apiErrorMessage(e, "Could not record the scope change"));
    }
    finally { setScopeSaving(false); }
  }

  async function saveMonitoring() {
    setMonSaving(true);
    setActionError(null);
    try {
      await api.patch(`/tprm/suppliers/${supplier.id}/monitoring`, { monitoringState: monState });
      setMonSaved(true);
      setTimeout(() => setMonSaved(false), 2500);
      setEditingMon(false);
      onUpdate();
    } catch (e) {
      setActionError(apiErrorMessage(e, "Could not save the monitoring dates"));
    }
    finally { setMonSaving(false); }
  }

  async function handleRemind(qId: string) {
    setReminding(true);
    try {
      const res = await api.post<{ emailedTo: string; expiresAt: string }>(`/tprm/questionnaires/${qId}/remind`, {});
      setRemindMsg({ id: qId, ok: true, text: `Reminder sent to ${res.emailedTo} with a new link valid until ${fmtDate(res.expiresAt)}.` });
      await loadData();
      onUpdate();
    } catch (e) {
      setRemindMsg({ id: qId, ok: false, text: apiErrorMessage(e, "Could not send the reminder") });
    } finally {
      setReminding(false);
    }
  }

  async function saveDetails(data: Partial<Supplier>) {
    setDetailsError(null);
    const err = await onSaveSupplier(data);
    if (err) setDetailsError(err);
    else setEditingDetails(false);
  }

  const openQuestionnaire = activeQ ? questionnaires.find((q) => q.id === activeQ) ?? null : null;

  // The subcontractor list question is never scored: the member records the list on the Assurance tab.
  const scoredQuestions = questions.filter((q) => q.answerType !== "subcontractors");
  const answeredCount = scoredQuestions.filter((q) => answers[q.text]).length;
  const totalQuestions = scoredQuestions.length;

  return (
    <>
      {/* Portal link sent modal */}
      {sendResult && (
        <div style={{ position: "fixed", inset: 0, zIndex: 300, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ ...floatingSheet, padding: 28, width: 460 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: theme.color.heading, marginBottom: 10, paddingBottom: 8, borderBottom: "1px solid var(--c-line)" }}>Questionnaire link ready</div>
            <p style={{ fontSize: 13, color: theme.color.subtle, lineHeight: 1.5, margin: "0 0 14px" }}>
              {sendResult.emailedTo
                ? <>Emailed to <strong>{sendResult.emailedTo}</strong>. You can also share the link directly:</>
                : <>No contact email is set for this supplier — share the link below directly:</>}
            </p>
            <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 12 }}>
              <input readOnly value={sendResult.portalUrl}
                onFocus={(e) => e.target.select()}
                style={{ ...inputStyle, flex: 1, fontSize: 12, fontFamily: "monospace" }} />
              <button
                onClick={() => { navigator.clipboard.writeText(sendResult.portalUrl); setLinkCopied(true); setTimeout(() => setLinkCopied(false), 2000); }}
                style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 12, padding: "7px 12px", borderRadius: 3, border: "1px solid var(--c-border)", background: "var(--c-panel)", color: theme.color.text, cursor: "pointer", flexShrink: 0 }}>
                <Copy size={12} /> {linkCopied ? "Copied!" : "Copy"}
              </button>
            </div>
            <div style={{ fontSize: 11, color: theme.color.faint, marginBottom: 16 }}>
              Single-use link, expires {fmtDate(sendResult.expiresAt)}. Re-sending later invalidates it.
            </div>
            <button onClick={() => setSendResult(null)} style={{ ...btnPrimary, padding: "8px 16px", fontSize: 13, cursor: "pointer" }}>
              Done
            </button>
          </div>
        </div>
      )}

      {/* Review + confidence modal */}
      {reviewingQId && (
        <div style={{ position: "fixed", inset: 0, zIndex: 300, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ ...floatingSheet, padding: 28, width: 380 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: theme.color.heading, marginBottom: 10, paddingBottom: 8, borderBottom: "1px solid var(--c-line)" }}>Mark as Reviewed</div>
            <p style={{ fontSize: 13, color: theme.color.subtle, lineHeight: 1.5, margin: "0 0 18px" }}>
              Rate your confidence in the answers provided by this third party.
            </p>
            <div style={{ marginBottom: 18 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: theme.color.muted, marginBottom: 10 }}>Data confidence</div>
              <div style={{ display: "flex", gap: 8 }}>
                {[1, 2, 3, 4, 5].map((n) => {
                  const labels = ["", "Very low", "Low", "Moderate", "High", "Very high"];
                  return (
                    <button key={n} onClick={() => setReviewConfidence(n)} style={{
                      flex: 1, padding: "8px 4px", borderRadius: 3, fontSize: 11, fontWeight: 600,
                      border: reviewConfidence === n ? `2px solid ${theme.color.accent}` : "1px solid var(--c-border)",
                      background: reviewConfidence === n ? "var(--c-accentBg)" : "var(--c-panel)",
                      color: reviewConfidence === n ? "var(--c-accentText)" : theme.color.muted,
                      cursor: "pointer", textAlign: "center",
                    }}>
                      {n}<br /><span style={{ fontWeight: 400 }}>{labels[n]}</span>
                    </button>
                  );
                })}
              </div>
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <button onClick={() => handleReviewWithConfidence(reviewingQId, reviewConfidence)} disabled={reviewBusy} style={{ ...btnPrimary, fontSize: 13, opacity: reviewBusy ? 0.6 : 1 }}>
                {reviewBusy ? "Saving…" : "Confirm review"}
              </button>
              <button onClick={() => setReviewingQId(null)} style={{ padding: "8px 14px", fontSize: 13, borderRadius: 3, border: "1px solid var(--c-border)", background: "transparent", color: theme.color.muted, cursor: "pointer" }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <div style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)",
        display: "flex", alignItems: "flex-start", justifyContent: "flex-end",
        zIndex: 200, padding: 0,
      }}>
        <div style={{
          ...floatingSheet,
          width: "min(720px, 100vw)", height: "100vh",
          display: "flex", flexDirection: "column", overflow: "hidden",
        }}>
          {/* Header */}
          <div style={{ padding: "18px 24px 0", flexShrink: 0 }}>
            <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 12 }}>
              <div>
                <div style={{ fontSize: 16, fontWeight: 700, color: theme.color.heading }}>{supplier.name}</div>
                <div style={{ display: "flex", gap: 10, marginTop: 4, flexWrap: "wrap", alignItems: "center" }}>
                  <Pill label={supplier.riskTier} colors={TIER_COLORS[supplier.riskTier] ?? { bg: "var(--c-faint)", text: "var(--c-muted)" }} />
                  <Pill label={supplier.status}   colors={STATUS_COLORS[supplier.status]   ?? { bg: "var(--c-faint)", text: "var(--c-muted)" }} />
                  {supplier.internalOwner && (
                    <span style={{ fontSize: 11, color: theme.color.muted }}>Owner: <strong style={{ color: theme.color.text }}>{supplier.internalOwner}</strong></span>
                  )}
                  {supplier.category && (
                    <span style={{ fontSize: 11, color: theme.color.muted }}>{supplier.category}</span>
                  )}
                </div>
              </div>
              <button onClick={onClose} style={{ background: "none", border: "none", color: theme.color.muted, cursor: "pointer", padding: 4, flexShrink: 0 }}><X size={18} /></button>
            </div>

            {/* Panel tabs */}
            <IndexTabs<PanelTab>
              items={[
                { id: "overview",       label: "Overview" },
                { id: "assessments",    label: "Assessments" },
                { id: "assurance",      label: "Assurance" },
                { id: "compromise",     label: "Compromise" },
                { id: "contracts",      label: "Contracts", count: (supplier.contracts ?? []).length || undefined },
                { id: "certifications", label: "Certifications" },
                { id: "assets",         label: "Assets", count: (supplier.assets ?? []).filter((a) => a.status === "active").length || undefined },
                { id: "scope",          label: "Scope Changes" },
                { id: "monitoring",     label: "Monitoring" },
              ]}
              value={panelTab}
              onChange={switchTab}
              ground="var(--c-panel)"
              style={{ marginBottom: 0 }}
            />
          </div>

          <div style={{ flex: 1, overflowY: "auto", padding: 24 }}>

            {actionError && (
              <div role="alert" style={{ ...text.hint, color: "var(--c-dangerText)", marginBottom: 16, padding: "10px 12px", borderRadius: 3, background: "var(--c-dangerBg)", border: "1px solid var(--c-danger)" }}>
                {actionError}
              </div>
            )}

            {/* ── Procurement gate (operational resilience): the decision itself is an action on the Overview tab ── */}
            {supplier.status === "prospective" && panelTab !== "overview" && (
              <div style={{ ...text.hint, marginBottom: 16, padding: "10px 12px", borderRadius: 3, background: "var(--c-panelAlt)", border: "1px solid var(--c-border)" }}>
                Prospective supplier: not yet approved through the procurement gate.{" "}
                <button type="button" onClick={() => switchTab("overview")} style={linkBtn}>Open the overview</button>
              </div>
            )}
            {supplier.status === "active" && supplier.approvedAt && (
              <div style={{ marginBottom: 16, fontSize: 12, color: theme.color.muted }}>
                Approved through the procurement gate on {fmtDate(supplier.approvedAt)}{supplier.approvalNote ? ` — ${supplier.approvalNote.trim().replace(/\.+$/, "")}` : ""}.
              </div>
            )}

            {/* ── Overview: the read-only view; the details form sits behind Edit ── */}
            {panelTab === "overview" && (
              editingDetails ? (
                <>
                  <TabHead title="Edit supplier details" />
                  {detailsError && <div style={{ ...text.hint, color: "var(--c-dangerText)", marginBottom: 10 }}>{detailsError}</div>}
                  <SupplierForm initial={supplier} onSave={saveDetails} onCancel={() => { setEditingDetails(false); setDetailsError(null); }} saving={savingSupplier} />
                </>
              ) : (
                <>
                  <TabHead>{canWrite && <EditButton label="Edit details" onClick={() => setEditingDetails(true)} />}</TabHead>
                  <OverviewTab supplier={supplier} contracts={supplier.contracts ?? []} onOpenTab={switchTab} concentration={concentration}
                    incidents={incidents} onRecordReview={canWrite ? () => setEditingDetails(true) : undefined} />
                  {supplier.status === "prospective" && <ProcurementGate supplier={supplier} />}
                  {canWrite && (
                    <div style={{ marginTop: 16 }}>
                      <RecordActions>
                        {supplier.status === "prospective" && <ProcurementDecision supplier={supplier} onUpdate={onUpdate} />}
                        <button type="button" onClick={onToggleDataProcessor} disabled={togglingDataProcessor}
                          title="The record of processing (GDPR RoPA) lists the suppliers marked as data processors"
                          style={{ ...btnSecondary, padding: "6px 12px", fontSize: 12, opacity: togglingDataProcessor ? 0.6 : 1 }}>
                          {supplier.isDataProcessor ? "Remove data processor flag" : "Mark as data processor"}
                        </button>
                        <button type="button" onClick={onDelete} style={{ ...btnDanger, padding: "6px 12px", fontSize: 12 }}>
                          Remove supplier
                        </button>
                      </RecordActions>
                    </div>
                  )}
                </>
              )
            )}

            {/* ── Assurance: signals, public domain checks, vendor rating, guest access, fourth parties ── */}
            {panelTab === "assurance" && (
              <SupplierAssurancePanel supplierId={supplier.id} supplierName={supplier.name} canWrite={canWrite} />
            )}

            {/* ── Compromise: what rests on the supplier if it is compromised (lib/supplierCompromise.ts) ── */}
            {panelTab === "compromise" && (
              <SupplierCompromisePanel supplierId={supplier.id} canWrite={canWrite} />
            )}

            {/* ── Contracts (lib/supplierContracts.ts) ── */}
            {panelTab === "contracts" && (
              <ContractsTab supplier={supplier} contracts={supplier.contracts ?? []} onChanged={onUpdate} canWrite={canWrite} onOpenTab={switchTab} />
            )}

            {/* ── Certifications tab (operational resilience) ── */}
            {panelTab === "certifications" && (
              <CertificationsTab supplier={supplier} onUpdate={onUpdate} canWrite={canWrite} />
            )}

            {/* ── Assets delivered (operational resilience asset register) ── */}
            {panelTab === "assets" && (
              <SupplierAssetsPanel supplier={supplier} />
            )}

            {/* ── Assessments tab: summaries in the list, a read-only view on open, actions at its foot ── */}
            {panelTab === "assessments" && openQuestionnaire && (
              <>
                <BackLink label="All assessments" onClick={() => setActiveQ(null)} />
                <div style={{ ...text.itemTitle, marginBottom: 6 }}>{openQuestionnaire.title}</div>
                <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center", marginBottom: 12 }}>
                  <Pill label={openQuestionnaire.status} colors={Q_STATUS_COLORS[openQuestionnaire.status] ?? { bg: "var(--c-faint)", text: "var(--c-muted)" }} />
                  <ConfidenceBadge level={openQuestionnaire.dataConfidence} />
                </div>
                <DetailSection title="Details">
                  <DetailFields fields={[
                    { label: "Status", value: openQuestionnaire.status.replace(/_/g, " ") },
                    { label: "Risk score", value: openQuestionnaire.riskScore != null ? `${openQuestionnaire.riskScore}%` : null },
                    { label: "Data confidence", value: openQuestionnaire.dataConfidence != null ? `${openQuestionnaire.dataConfidence} of 5` : null },
                    { label: "Created", value: fmtDate(openQuestionnaire.createdAt) },
                    { label: "Due", value: openQuestionnaire.dueDate ? fmtDateOnly(openQuestionnaire.dueDate) : null },
                    { label: "Returned", value: openQuestionnaire.returnedAt ? fmtDate(openQuestionnaire.returnedAt) : null },
                    { label: "Completed by", value: openQuestionnaire.respondentName || openQuestionnaire.respondentEmail
                      ? `${openQuestionnaire.respondentName ?? ""}${openQuestionnaire.respondentName && openQuestionnaire.respondentEmail ? " " : ""}${openQuestionnaire.respondentEmail ? `(${openQuestionnaire.respondentEmail})` : ""}`
                      : null },
                  ]} />
                </DetailSection>
                <DetailSection title="Answers">
                  <QAnswers answers={openQuestionnaire.answers} />
                </DetailSection>
                {remindMsg && remindMsg.id === openQuestionnaire.id && (
                  <div style={{ ...text.hint, color: remindMsg.ok ? "var(--c-successText)" : "var(--c-dangerText)", marginBottom: 8 }}>{remindMsg.text}</div>
                )}
                {canWrite && (openQuestionnaire.status === "returned" || openQuestionnaire.status === "draft" || openQuestionnaire.status === "sent") && (
                  <RecordActions note={openQuestionnaire.status === "sent" ? "A reminder emails the supplier's contact a new link and keeps the date it was first sent; resending issues a fresh link. Either way the old link stops working." : undefined}>
                    {openQuestionnaire.status === "returned" && (
                      <button type="button" onClick={() => { setReviewingQId(openQuestionnaire.id); setReviewConfidence(3); }} style={{ ...btnPrimary, padding: "6px 12px", fontSize: 12 }}>
                        Mark reviewed
                      </button>
                    )}
                    {(openQuestionnaire.status === "draft" || openQuestionnaire.status === "sent") && (
                      <button type="button" onClick={() => handleResend(openQuestionnaire.id)} disabled={sending}
                        style={{ ...btnSecondary, padding: "6px 12px", fontSize: 12, display: "inline-flex", alignItems: "center", gap: 5, opacity: sending ? 0.6 : 1 }}>
                        <Send size={12} /> {openQuestionnaire.status === "sent" ? "Resend link" : "Send to supplier"}
                      </button>
                    )}
                    {openQuestionnaire.status === "sent" && (
                      <button type="button" onClick={() => handleRemind(openQuestionnaire.id)} disabled={reminding || !supplier.contactEmail}
                        title={supplier.contactEmail ? undefined : "Add a contact email to the supplier to send a reminder"}
                        style={{ ...btnSecondary, padding: "6px 12px", fontSize: 12, opacity: reminding || !supplier.contactEmail ? 0.6 : 1 }}>
                        {reminding ? "Sending…" : "Send reminder"}
                      </button>
                    )}
                    {openQuestionnaire.status === "draft" && (
                      <button type="button" onClick={() => handleDeleteDraft(openQuestionnaire)} style={{ ...btnDanger, padding: "6px 12px", fontSize: 12 }}>Delete draft</button>
                    )}
                  </RecordActions>
                )}
              </>
            )}

            {panelTab === "assessments" && !openQuestionnaire && (
              <>
                {/* Supplier context */}
                {supplier.riskBasis && (
                  <div style={{ ...card, marginBottom: 16, padding: "12px 14px", background: "var(--c-panelAlt)", fontSize: 12, color: theme.color.subtle, lineHeight: 1.5 }}>
                    <strong style={{ color: theme.color.muted }}>Risk rationale:</strong> {supplier.riskBasis}
                  </div>
                )}

                {loading ? (
                  <div style={{ fontSize: 13, color: theme.color.muted }}>Loading…</div>
                ) : questionnaires.length > 0 ? (
                  <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 20 }}>
                    {questionnaires.map((q) => (
                      <button key={q.id} type="button" onClick={() => setActiveQ(q.id)} title={`Open ${q.title}`} style={rowBtn}>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 13, fontWeight: 600, color: theme.color.heading, marginBottom: 4 }}>{q.title}</div>
                            <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
                              <Pill label={q.status} colors={Q_STATUS_COLORS[q.status] ?? { bg: "var(--c-faint)", text: "var(--c-muted)" }} />
                              {q.riskScore !== null && (
                                <span style={{ fontSize: 11, color: q.riskScore >= 70 ? "var(--c-successText)" : q.riskScore >= 40 ? "var(--c-warningText)" : "var(--c-dangerText)" }}>
                                  Risk score: <strong>{q.riskScore}%</strong>
                                </span>
                              )}
                              <ConfidenceBadge level={q.dataConfidence} />
                              <span style={{ fontSize: 11, color: theme.color.faint }}>{fmtDate(q.createdAt)}</span>
                              {q.dueDate && <span style={{ fontSize: 11, color: theme.color.faint }}>Due: {fmtDateOnly(q.dueDate)}</span>}
                            </div>
                          </div>
                          <ChevronRight size={16} color="var(--c-muted)" style={{ flexShrink: 0 }} />
                        </div>
                      </button>
                    ))}
                  </div>
                ) : (
                  <div style={{ ...emptyFrame, marginBottom: 20, color: theme.color.muted, fontSize: 13 }}>
                    {canWrite ? "No assessments yet. Create one below." : "No assessments yet."}
                  </div>
                )}

                {/* Starting an assessment: the list-level "add", for people who may write */}
                {canWrite && (
                  <>
                    {/* Questionnaire choice — the built-in tier set by default, or one of the organisation's own */}
                    <div style={{ marginBottom: 16, padding: "10px 14px", borderRadius: 3, background: "var(--c-accentBg)", border: "1px solid var(--c-accent)44", fontSize: 12, color: "var(--c-accentText)" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                        <span style={{ fontWeight: 600, whiteSpace: "nowrap" }}>New assessment using</span>
                        <select
                          value={selectedSet?.key ?? defaultSetKey}
                          onChange={(e) => { setSetKey(e.target.value); setAnswers({}); }}
                          disabled={creating || catalogue.builtin.length === 0}
                          style={{ ...inputStyle, flex: 1, minWidth: 220, fontSize: 12, padding: "6px 10px" }}
                        >
                          <optgroup label="Standard (built in)">
                            {catalogue.builtin.map((b) => (
                              <option key={b.key} value={b.key}>{b.name}{b.tier === supplier.riskTier ? " — recommended" : ""}</option>
                            ))}
                          </optgroup>
                          {catalogue.templates.length > 0 && (
                            <optgroup label="Your questionnaires">
                              {catalogue.templates.map((t) => (
                                <option key={t.id} value={t.id}>{t.name} ({t.questions.length} questions{t.riskTier ? `, ${t.riskTier} risk` : ""})</option>
                              ))}
                            </optgroup>
                          )}
                        </select>
                        <button onClick={onManageQuestionnaires} style={{ background: "none", border: "none", color: "var(--c-accentText)", cursor: "pointer", fontSize: 12, textDecoration: "underline", padding: 0, whiteSpace: "nowrap" }}>
                          Manage questionnaires
                        </button>
                      </div>
                      <label style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 8, flexWrap: "wrap" }}>
                        <span style={{ fontWeight: 600, whiteSpace: "nowrap" }}>Due back by</span>
                        <input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} disabled={creating}
                          style={{ ...inputStyle, width: "auto", fontSize: 12, padding: "6px 10px" }} />
                        <span style={{ color: theme.color.muted }}>Optional</span>
                      </label>
                      <div style={{ marginTop: 6 }}>
                        Using a <strong>{questions.length}-question questionnaire</strong> for this {supplier.riskTier}-risk supplier.
                        The questions are scaled to the supplier's risk tier.
                      </div>
                    </div>
                {!creating ? (
                  <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                    <button onClick={handleSendToSupplier} disabled={sending}
                      style={{ ...btnPrimary, display: "flex", alignItems: "center", gap: 6, cursor: sending ? "not-allowed" : "pointer", opacity: sending ? 0.6 : 1 }}>
                      <Send size={14} /> {sending ? "Sending…" : "Send to supplier"}
                    </button>
                    <button onClick={() => setCreating(true)} disabled={questions.length === 0}
                      style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 16px", fontSize: 13, borderRadius: 3, border: "1px solid var(--c-border)", background: "var(--c-panel)", color: theme.color.text, cursor: questions.length === 0 ? "not-allowed" : "pointer", opacity: questions.length === 0 ? 0.6 : 1 }}>
                      <Plus size={14} /> Internal assessment
                    </button>
                  </div>
                ) : (
                  <div style={card}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: theme.color.heading, marginBottom: 2 }}>
                      New risk assessment: {selectedSet?.name ?? `${tierLabel(supplier.riskTier)} tier`}
                    </div>
                    <p style={{ fontSize: 12, color: theme.color.subtle, margin: "0 0 14px", lineHeight: 1.6 }}>
                      {answeredCount}/{totalQuestions} answered. Saves as draft if incomplete.
                    </p>

                    <RulerProgress value={answeredCount} max={totalQuestions} label="Questions answered" style={{ marginBottom: 16 }} />

                    <div style={{ display: "flex", flexDirection: "column", gap: 14, marginBottom: 16 }}>
                      {questions.map((q, idx) => {
                        const adverseOption = q.adverseAnswer === "yes" ? "Yes" : "No";
                        return (
                          <div key={q.text}>
                            <div style={{ fontSize: 12, color: theme.color.text, marginBottom: 6, lineHeight: 1.4 }}>
                              <span style={{ color: theme.color.faint, marginRight: 6 }}>{idx + 1}.</span>{q.text}
                              {q.category && <span style={{ marginLeft: 8, fontSize: 10, color: theme.color.faint, textTransform: "uppercase", letterSpacing: "0.04em" }}>{q.category}</span>}
                            </div>
                            {q.answerType === "subcontractors" ? (
                              <div style={{ fontSize: 11, color: theme.color.muted, margin: "-2px 0 0 18px", lineHeight: 1.4 }}>
                                Not scored. Record the supplier's subcontractors on the Assurance tab with Add subcontractor.
                              </div>
                            ) : (<>
                            {q.guidance && (
                              <div style={{ fontSize: 11, color: theme.color.muted, margin: "-2px 0 6px 18px", lineHeight: 1.4 }}>{q.guidance}</div>
                            )}
                            <div style={{ display: "flex", gap: 6 }}>
                              {["Yes", "No", "Partial", "N/A"].map((opt) => (
                                <button
                                  key={opt}
                                  onClick={() => setAnswers((a) => ({ ...a, [q.text]: opt }))}
                                  style={{
                                    padding: "4px 12px", borderRadius: 3, fontSize: 12,
                                    border: answers[q.text] === opt ? `2px solid ${theme.color.accent}` : "1px solid var(--c-border)",
                                    background: answers[q.text] === opt ? "var(--c-accentBg)" : "var(--c-panel)",
                                    color: answers[q.text] === opt
                                      ? "var(--c-accentText)"
                                      : opt === adverseOption ? "var(--c-dangerText)" : theme.color.muted,
                                    cursor: "pointer", fontWeight: answers[q.text] === opt ? 600 : 400,
                                  }}
                                >
                                  {opt}
                                </button>
                              ))}
                            </div>
                            </>)}
                          </div>
                        );
                      })}
                    </div>
                    <div style={{ display: "flex", gap: 10 }}>
                      <button onClick={handleCreateQuestionnaire} disabled={saving}
                        style={{ ...btnPrimary, padding: "8px 16px", fontSize: 13, cursor: saving ? "not-allowed" : "pointer", opacity: saving ? 0.6 : 1 }}>
                        {saving ? "Saving…" : "Save assessment"}
                      </button>
                      <button onClick={() => { setCreating(false); setAnswers({}); }}
                        style={{ padding: "8px 14px", fontSize: 13, borderRadius: 3, border: "1px solid var(--c-border)", background: "var(--c-panel)", color: theme.color.muted, cursor: "pointer" }}>
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
                  </>
                )}
              </>
            )}

            {/* ── Scope Changes tab (Gartner Insight 3) ── */}
            {panelTab === "scope" && (
              <>
                <div style={{ marginBottom: 16, padding: "10px 14px", borderRadius: 3, background: "var(--c-panelAlt)", border: "1px solid var(--c-border)", fontSize: 12, color: theme.color.subtle, lineHeight: 1.5 }}>
                  Log changes to the scope of this supplier relationship, such as new data it can reach, new locations or new services, so the risk is reassessed when the relationship changes.
                </div>

                {loading ? (
                  <div style={{ fontSize: 13, color: theme.color.muted }}>Loading…</div>
                ) : scopeChanges.length > 0 ? (
                  <div style={{ marginBottom: 20 }}>
                    {scopeChanges.map((sc) => (
                      <div key={sc.id} style={{ ...card, marginBottom: 8, padding: "12px 14px", display: "flex", gap: 12 }}>
                        <div style={{ flexShrink: 0, marginTop: 2 }}>
                          <GitBranch size={14} color="var(--c-accentText)" />
                        </div>
                        <div style={{ flex: 1 }}>
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8 }}>
                            <span style={{ fontSize: 12, fontWeight: 600, color: theme.color.heading }}>
                              {SCOPE_CHANGE_TYPES[sc.changeType] ?? sc.changeType}
                            </span>
                            <span style={{ fontSize: 11, color: theme.color.faint, whiteSpace: "nowrap" }}>
                              {fmtDate(sc.changedAt)}
                            </span>
                          </div>
                          <div style={{ fontSize: 12, color: theme.color.subtle, marginTop: 3, lineHeight: 1.5 }}>
                            {sc.description}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{ ...emptyFrame, marginBottom: 20, color: theme.color.muted, fontSize: 13 }}>
                    No scope changes logged yet.
                  </div>
                )}

                {!canWrite ? null : !addingScope ? (
                  <button onClick={() => setAddingScope(true)} style={{ ...btnPrimary, display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
                    <Plus size={14} /> Log scope change
                  </button>
                ) : (
                  <div style={card}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: theme.color.heading, marginBottom: 14 }}>Log scope change</div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                      <label style={{ display: "flex", flexDirection: "column", gap: 5, fontSize: 13 }}>
                        <span style={{ fontWeight: 500, color: theme.color.text }}>Change type</span>
                        <select value={scopeForm.changeType} onChange={(e) => setScopeForm((f) => ({ ...f, changeType: e.target.value }))} style={{ ...inputStyle, fontSize: 13 }}>
                          {Object.entries(SCOPE_CHANGE_TYPES).map(([k, v]) => (
                            <option key={k} value={k}>{v}</option>
                          ))}
                        </select>
                      </label>
                      <label style={{ display: "flex", flexDirection: "column", gap: 5, fontSize: 13 }}>
                        <span style={{ fontWeight: 500, color: theme.color.text }}>Description *</span>
                        <textarea
                          value={scopeForm.description}
                          onChange={(e) => setScopeForm((f) => ({ ...f, description: e.target.value }))}
                          placeholder="Describe what changed and any risk implications…"
                          rows={3}
                          style={{ ...inputStyle, fontSize: 13, resize: "vertical" }}
                        />
                      </label>
                    </div>
                    <div style={{ display: "flex", gap: 10, marginTop: 14 }}>
                      <button onClick={addScopeChange} disabled={scopeSaving || !scopeForm.description.trim()}
                        style={{ ...btnPrimary, fontSize: 13, opacity: scopeSaving || !scopeForm.description.trim() ? 0.6 : 1, cursor: scopeSaving ? "not-allowed" : "pointer" }}>
                        {scopeSaving ? "Saving…" : "Log change"}
                      </button>
                      <button onClick={() => { setAddingScope(false); setScopeForm({ changeType: "service_expansion", description: "" }); }}
                        style={{ padding: "8px 14px", fontSize: 13, borderRadius: 3, border: "1px solid var(--c-border)", background: "var(--c-panel)", color: theme.color.muted, cursor: "pointer" }}>
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}

            {/* ── Monitoring tab (Gartner Insight 3) ── */}
            {panelTab === "monitoring" && (
              <>
                <div style={{ marginBottom: 16, padding: "10px 14px", borderRadius: 3, background: "var(--c-panelAlt)", border: "1px solid var(--c-border)", fontSize: 12, color: theme.color.subtle, lineHeight: 1.5 }}>
                  Track when each ongoing monitoring activity was last done and when it is next due.
                </div>

                <TabHead title={editingMon ? "Edit monitoring dates" : undefined}>
                  {canWrite && !editingMon && <EditButton label="Edit dates" onClick={() => setEditingMon(true)} />}
                  {monSaved && !editingMon && <span style={{ ...text.hint, color: "var(--c-successText)" }}>Saved</span>}
                </TabHead>

                <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 20 }}>
                  {MONITORING_ACTIVITIES.map((act) => {
                    const state = monState[act.key] ?? { lastDone: null, nextDue: null, notes: "" };
                    const overdue = isOverdue(state.nextDue);
                    return (
                      <div key={act.key} style={{ ...card, padding: "14px 16px" }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, marginBottom: 8 }}>
                          <div>
                            <div style={{ fontSize: 13, fontWeight: 600, color: theme.color.heading }}>{act.label}</div>
                            <div style={{ fontSize: 11, color: theme.color.faint }}>Typical cadence: {act.typical}</div>
                          </div>
                          {state.nextDue && (
                            <span style={{ fontSize: 11, fontWeight: 600, color: overdue ? "var(--c-dangerText)" : "var(--c-successText)", whiteSpace: "nowrap" }}>
                              {overdue ? "OVERDUE" : "Due " + fmtDateOnly(state.nextDue)}
                            </span>
                          )}
                        </div>
                        {!editingMon ? (
                          <DetailFields fields={[
                            { label: "Last completed", value: state.lastDone ? fmtDateOnly(state.lastDone) : null },
                            { label: "Next due", value: state.nextDue ? fmtDateOnly(state.nextDue) : null },
                          ]} />
                        ) : (
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                          <label style={{ display: "flex", flexDirection: "column", gap: 3, fontSize: 12 }}>
                            <span style={{ color: theme.color.muted }}>Last completed</span>
                            <input
                              type="date"
                              value={state.lastDone?.split("T")[0] ?? ""}
                              onChange={(e) => setMonState((ms) => ({
                                ...ms,
                                [act.key]: { ...state, lastDone: e.target.value || null },
                              }))}
                              style={{ ...inputStyle, fontSize: 12 }}
                            />
                          </label>
                          <label style={{ display: "flex", flexDirection: "column", gap: 3, fontSize: 12 }}>
                            <span style={{ color: theme.color.muted }}>Next due</span>
                            <input
                              type="date"
                              value={state.nextDue?.split("T")[0] ?? ""}
                              onChange={(e) => setMonState((ms) => ({
                                ...ms,
                                [act.key]: { ...state, nextDue: e.target.value || null },
                              }))}
                              style={{ ...inputStyle, fontSize: 12 }}
                            />
                          </label>
                        </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {editingMon && (
                  <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                    <button onClick={saveMonitoring} disabled={monSaving}
                      style={{ ...btnPrimary, fontSize: 13, opacity: monSaving ? 0.6 : 1, cursor: monSaving ? "not-allowed" : "pointer" }}>
                      {monSaving ? "Saving…" : "Save monitoring dates"}
                    </button>
                    <button type="button" onClick={() => { setMonState(supplier.monitoringState ?? {}); setEditingMon(false); }} style={{ ...btnSecondary, padding: "8px 14px" }}>
                      Cancel
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

// ── Procurement gate ──────────────────────────────────────────────────────────
// A prospective supplier is activated here, never by editing its status: the API
// refuses until a scored questionnaire or a documented risk basis exists.

// Mirror of backend/src/lib/procurementGate.ts: a one-word basis is a placeholder, not a reason.
const RISK_BASIS_MIN_CHARS = 20;

const gateReady = (supplier: Supplier) => {
  const scored = supplier.questionnaires.some((q) => (q.status === "returned" || q.status === "reviewed") && q.riskScore != null);
  return { scored, ready: scored || (supplier.riskBasis ?? "").trim().length >= RISK_BASIS_MIN_CHARS };
};

/** What the gate needs, read-only; the decision itself is ProcurementDecision in the Actions block. */
function ProcurementGate({ supplier }: { supplier: Supplier }) {
  const { scored, ready } = gateReady(supplier);
  return (
    <DetailSection title="Procurement gate">
      <div style={{ ...text.hint, lineHeight: 1.5 }}>
        {ready
          ? `Due diligence is on file (${scored ? "scored questionnaire" : "documented risk basis"}). Approve to make the supplier active, or reject.`
          : `Send and score a questionnaire, or write down why the supplier is on its risk tier (at least ${RISK_BASIS_MIN_CHARS} characters, under Edit details), before approving.`}
      </div>
    </DetailSection>
  );
}

function ProcurementDecision({ supplier, onUpdate }: { supplier: Supplier; onUpdate: () => void }) {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState<"approve" | "reject" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { ready } = gateReady(supplier);
  async function decide(action: "approve" | "reject") {
    setBusy(action); setError(null);
    try { await api.post(`/tprm/suppliers/${supplier.id}/${action}`, { note: note || undefined }); onUpdate(); }
    catch (e) { setError(apiErrorMessage(e, "Could not record the decision")); }
    finally { setBusy(null); }
  }
  return (
    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", width: "100%" }}>
      <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Decision note (who approved, conditions)" aria-label="Decision note" style={{ ...inputStyle, fontSize: 12, flex: 1, minWidth: 220, width: "auto" }} />
      <button type="button" disabled={!ready || busy !== null} onClick={() => decide("approve")} style={{ ...btnPrimary, padding: "6px 12px", fontSize: 12, opacity: !ready || busy ? 0.6 : 1 }}>{busy === "approve" ? "Approving…" : "Approve supplier"}</button>
      <button type="button" disabled={busy !== null} onClick={() => decide("reject")} style={{ ...btnDanger, padding: "6px 12px", fontSize: 12 }}>{busy === "reject" ? "Rejecting…" : "Reject supplier"}</button>
      {error && <div style={{ ...text.hint, color: "var(--c-dangerText)", width: "100%" }}>{error}</div>}
    </div>
  );
}

// ── Certifications ────────────────────────────────────────────────────────────

function CertificationsTab({ supplier, onUpdate, canWrite }: { supplier: Supplier; onUpdate: () => void; canWrite: boolean }) {
  const empty = { standard: "iso27001", certificateRef: "", issuer: "", scope: "", issuedAt: "", expiresAt: "", url: "", verified: false, notes: "" };
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // The certification whose read-only view is open.
  const [viewing, setViewing] = useState<string | null>(null);
  // The certification the form is editing (null: the form adds a new one).
  const [editingId, setEditingId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const certs = supplier.certifications ?? [];
  const now = Date.now();
  const open = viewing ? certs.find((c) => c.id === viewing) ?? null : null;

  async function submit(e: React.FormEvent) {
    e.preventDefault(); setSaving(true); setError(null);
    try {
      // Every field is sent, so an emptied one is cleared rather than kept.
      const body = {
        standard: form.standard, certificateRef: form.certificateRef || null, issuer: form.issuer || null, scope: form.scope || null,
        issuedAt: form.issuedAt ? new Date(form.issuedAt).toISOString() : null, expiresAt: form.expiresAt ? new Date(form.expiresAt).toISOString() : null,
        url: form.url || "", verified: form.verified, notes: form.notes || null,
      };
      const r = editingId
        ? await api.put<{ certification?: { id: string } }>(`/tprm/suppliers/${supplier.id}/certifications/${editingId}`, body)
        : await api.post<{ certification?: { id: string } }>(`/tprm/suppliers/${supplier.id}/certifications`, body);
      setAdding(false); setForm(empty); setEditingId(null);
      if (r?.certification?.id) setViewing(r.certification.id);
      onUpdate();
    } catch (err) { setError(apiErrorMessage(err, "Could not save the certification")); }
    finally { setSaving(false); }
  }

  async function remove(c: SupplierCertification) {
    if (!window.confirm(`Remove ${certificationLabel(c.standard)}?`)) return;
    setBusy(true); setError(null);
    try { await api.del(`/tprm/suppliers/${supplier.id}/certifications/${c.id}`); setViewing(null); onUpdate(); }
    catch (err) { setError(apiErrorMessage(err, "Could not remove the certification")); }
    finally { setBusy(false); }
  }

  function startEdit(c: SupplierCertification) {
    setForm({
      standard: c.standard, certificateRef: c.certificateRef ?? "", issuer: c.issuer ?? "", scope: c.scope ?? "",
      issuedAt: c.issuedAt ? c.issuedAt.slice(0, 10) : "", expiresAt: c.expiresAt ? c.expiresAt.slice(0, 10) : "",
      url: c.url ?? "", verified: !!c.verifiedAt, notes: c.notes ?? "",
    });
    setEditingId(c.id); setAdding(true); setViewing(null); setError(null);
  }

  async function toggleVerified(c: SupplierCertification) {
    setBusy(true); setError(null);
    try { await api.put(`/tprm/suppliers/${supplier.id}/certifications/${c.id}`, { verified: !c.verifiedAt }); onUpdate(); }
    catch (err) { setError(apiErrorMessage(err, "Could not update the certification")); }
    finally { setBusy(false); }
  }

  if (open) {
    const expired = open.expiresAt && new Date(open.expiresAt).getTime() < now;
    return (
      <>
        <BackLink label="All certifications" onClick={() => { setViewing(null); setError(null); }} />
        <div style={{ ...text.itemTitle, marginBottom: 4 }}>
          {certificationLabel(open.standard)}{open.certificateRef ? <span style={{ fontWeight: 400, color: theme.color.muted }}> · {open.certificateRef}</span> : null}
        </div>
        <DetailSection title="Details">
          <DetailFields fields={[
            { label: "Standard", value: certificationLabel(open.standard) },
            { label: "Certificate reference", value: open.certificateRef },
            { label: "Issuer / certification body", value: open.issuer },
            { label: "Issued", value: open.issuedAt ? fmtDateOnly(open.issuedAt) : null },
            { label: expired ? "Expired" : "Expires", value: open.expiresAt ? <span style={{ color: expired ? "var(--c-dangerText)" : undefined }}>{fmtDateOnly(open.expiresAt)}</span> : "No expiry recorded" },
            { label: "Verified against the issuer's register", value: open.verifiedAt ? `Yes, ${fmtDate(open.verifiedAt)}` : "No" },
            { label: "Certificate", value: safeHref(open.url) ? <a href={safeHref(open.url)} target="_blank" rel="noopener noreferrer" style={{ color: "var(--c-accentText)" }}>Open the certificate</a> : null },
            { label: "Scope", value: open.scope, wide: true, multiline: true },
            { label: "Notes", value: open.notes, wide: true, multiline: true },
          ]} />
        </DetailSection>
        {error && <div style={{ ...text.hint, color: "var(--c-dangerText)", marginBottom: 8 }}>{error}</div>}
        {canWrite && (
          <RecordActions>
            <button type="button" disabled={busy} onClick={() => startEdit(open)} style={{ ...btnSecondary, padding: "6px 12px", fontSize: 12, opacity: busy ? 0.6 : 1 }}>Edit</button>
            <button type="button" disabled={busy} onClick={() => toggleVerified(open)} style={{ ...btnSecondary, padding: "6px 12px", fontSize: 12, opacity: busy ? 0.6 : 1 }}>{open.verifiedAt ? "Mark not verified" : "Mark verified"}</button>
            <button type="button" disabled={busy} onClick={() => remove(open)} style={{ ...btnDanger, padding: "6px 12px", fontSize: 12, opacity: busy ? 0.6 : 1 }}>Remove certification</button>
          </RecordActions>
        )}
      </>
    );
  }

  return (
    <>
      <div style={{ marginBottom: 16, padding: "10px 14px", borderRadius: 3, background: "var(--c-panelAlt)", border: "1px solid var(--c-border)", fontSize: 12, color: theme.color.subtle, lineHeight: 1.5 }}>
        The certifications and attestations the supplier holds, with their expiry. Expiring and expired certificates appear on the Resilience attention list so renewals are chased; a verified certificate is one you have checked against the issuer's register.
      </div>
      {certs.length === 0 ? (
        <div style={{ fontSize: 13, color: theme.color.muted, marginBottom: 14 }}>No certifications recorded.</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 14 }}>
          {certs.map((c) => {
            const expired = c.expiresAt && new Date(c.expiresAt).getTime() < now;
            const soon = !expired && c.expiresAt && new Date(c.expiresAt).getTime() < now + 60 * 86_400_000;
            return (
              <button key={c.id} type="button" onClick={() => setViewing(c.id)} title={`Open ${certificationLabel(c.standard)}`} style={{ ...rowBtn, padding: "10px 12px", border: `1px solid ${expired ? "var(--c-danger)" : "var(--c-border)"}` }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: theme.color.text }}>
                    {certificationLabel(c.standard)}{c.certificateRef ? <span style={{ fontWeight: 400, color: theme.color.muted }}> · {c.certificateRef}</span> : null}
                    {c.verifiedAt ? <span style={{ marginLeft: 8, fontSize: 10, fontWeight: 700, color: "var(--c-successText)" }}>Verified</span> : null}
                  </div>
                  <ChevronRight size={15} color="var(--c-muted)" style={{ flexShrink: 0 }} />
                </div>
                <div style={{ fontSize: 12, color: theme.color.muted, marginTop: 4 }}>
                  {c.issuer ? `${c.issuer} · ` : ""}{c.issuedAt ? `issued ${fmtDateOnly(c.issuedAt)} · ` : ""}
                  <span style={{ color: expired ? "var(--c-dangerText)" : soon ? "var(--c-warningText)" : undefined }}>{c.expiresAt ? `${expired ? "expired" : "expires"} ${fmtDateOnly(c.expiresAt)}` : "no expiry recorded"}</span>
                </div>
              </button>
            );
          })}
        </div>
      )}
      {!canWrite ? null : !adding ? (
        <button type="button" onClick={() => setAdding(true)} style={{ ...btnPrimary, fontSize: 13 }}><Plus size={14} style={{ verticalAlign: -2, marginRight: 6 }} />Add certification</button>
      ) : (
        <form onSubmit={submit} style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, padding: 14, border: "1px solid var(--c-border)", borderRadius: 3 }}>
          {editingId && <div style={{ ...text.itemTitle, gridColumn: "1 / -1" }}>Edit certification</div>}
          <label style={{ fontSize: 12, display: "grid", gap: 4 }}>Standard
            <select value={form.standard} onChange={(e) => setForm((f) => ({ ...f, standard: e.target.value }))} style={{ ...inputStyle, fontSize: 13 }}>{CERTIFICATION_STANDARDS.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}</select>
          </label>
          <label style={{ fontSize: 12, display: "grid", gap: 4 }}>Certificate reference<input value={form.certificateRef} onChange={(e) => setForm((f) => ({ ...f, certificateRef: e.target.value }))} style={{ ...inputStyle, fontSize: 13 }} /></label>
          <label style={{ fontSize: 12, display: "grid", gap: 4 }}>Issuer / certification body<input value={form.issuer} onChange={(e) => setForm((f) => ({ ...f, issuer: e.target.value }))} style={{ ...inputStyle, fontSize: 13 }} /></label>
          <label style={{ fontSize: 12, display: "grid", gap: 4 }}>Certificate URL (https)<input value={form.url} onChange={(e) => setForm((f) => ({ ...f, url: e.target.value }))} placeholder="https://" style={{ ...inputStyle, fontSize: 13 }} /></label>
          <label style={{ fontSize: 12, display: "grid", gap: 4 }}>Issued<input type="date" value={form.issuedAt} onChange={(e) => setForm((f) => ({ ...f, issuedAt: e.target.value }))} style={{ ...inputStyle, fontSize: 13 }} /></label>
          <label style={{ fontSize: 12, display: "grid", gap: 4 }}>Expires<input type="date" value={form.expiresAt} onChange={(e) => setForm((f) => ({ ...f, expiresAt: e.target.value }))} style={{ ...inputStyle, fontSize: 13 }} /></label>
          <label style={{ fontSize: 12, display: "grid", gap: 4, gridColumn: "1 / -1" }}>Scope<input value={form.scope} onChange={(e) => setForm((f) => ({ ...f, scope: e.target.value }))} placeholder="What the certificate covers" style={{ ...inputStyle, fontSize: 13 }} /></label>
          <label style={{ fontSize: 12, display: "grid", gap: 4, gridColumn: "1 / -1" }}>Notes<textarea rows={2} value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} style={{ ...inputStyle, fontSize: 13, resize: "vertical" }} /></label>
          <label style={{ fontSize: 12, display: "flex", gap: 8, alignItems: "center", gridColumn: "1 / -1" }}><input type="checkbox" checked={form.verified} onChange={(e) => setForm((f) => ({ ...f, verified: e.target.checked }))} />Verified against the issuer's register</label>
          {error && <div style={{ gridColumn: "1 / -1", fontSize: 12, color: "var(--c-dangerText)" }}>{error}</div>}
          <div style={{ gridColumn: "1 / -1", display: "flex", gap: 8 }}>
            <button type="submit" disabled={saving} style={{ ...btnPrimary, fontSize: 13, opacity: saving ? 0.6 : 1 }}>{saving ? "Saving…" : "Save"}</button>
            <button type="button" onClick={() => { const back = editingId; setAdding(false); setError(null); setEditingId(null); setForm(empty); if (back) setViewing(back); }} style={{ padding: "8px 14px", fontSize: 13, borderRadius: 3, border: "1px solid var(--c-border)", background: "transparent", color: theme.color.muted, cursor: "pointer" }}>Cancel</button>
          </div>
        </form>
      )}
    </>
  );
}

// ── Overview ──────────────────────────────────────────────────────────────────
// Everything known about the relationship on one page: who it is, who owns it,
// what it delivers, what has been assessed and what is agreed. Each block links
// through to the register that holds the detail.

function DetailRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: "flex", gap: 12, padding: "5px 0", borderBottom: "1px solid var(--c-border)" }}>
      <div style={{ ...text.label, marginBottom: 0, lineHeight: 1.6, width: 168, flexShrink: 0 }}>{label}</div>
      <div style={{ ...text.hint, color: theme.color.text, lineHeight: 1.6, minWidth: 0, wordBreak: "break-word" }}>{children}</div>
    </div>
  );
}

const dash = <span style={{ color: theme.color.faint }}>—</span>;

/** Whether the supplier told the organisation inside its agreement's window; null when no agreement sets one. */
function NoticeVerdict({ incident }: { incident: SupplierIncident }) {
  const n = incident.notice;
  const agreement = n.contractTitle ?? "the agreement";
  if (n.state === "no_window") return null;
  if (n.state === "on_time") {
    return <span style={{ ...text.hint, color: "var(--c-successText)" }} title={`Within the ${n.windowHours} h window of ${agreement}`}>Told you in time</span>;
  }
  if (n.state === "late") {
    const hours = n.lateByHours != null ? Number(n.lateByHours.toFixed(1)) : null;
    return <span style={{ ...text.hint, color: "var(--c-dangerText)", fontWeight: 600 }}>Told you {hours ?? "?"} h late against {agreement} ({n.windowHours} h)</span>;
  }
  return (
    <span style={{ ...text.hint, color: "var(--c-warningText)" }}>
      When the supplier told you is not recorded. <PortalLink to={`/resilience/incidents?incident=${encodeURIComponent(incident.id)}`} style={{ color: "var(--c-accentText)" }}>Record it on the incident</PortalLink>
    </span>
  );
}

function OverviewTab({ supplier, contracts, onOpenTab, concentration, incidents, onRecordReview }: {
  supplier: Supplier; contracts: Contract[]; onOpenTab: (t: PanelTab) => void; concentration?: SupplierConcentrationRow;
  incidents: SupplierIncident[] | null;
  /** Opens the details form, where the review dates are recorded (tprm:write only). */
  onRecordReview?: () => void;
}) {
  const rel = relationshipOf(supplier);
  const live = contracts.filter((c) => contractInForce(contractTiming(c).standing));
  const coverage = combinedTermCoverage(contracts, rel);
  const latest = supplier.questionnaires?.[0] ?? null;
  const activeAssets = (supplier.assets ?? []).filter((a) => a.status === "active");
  const certs = supplier.certifications ?? [];
  const expiredCerts = certs.filter((c) => c.expiresAt && new Date(c.expiresAt).getTime() < Date.now());
  const actions = useLinkedActions("supplierId", supplier.id);
  const important = supplier.riskTier === "high" || supplier.riskTier === "critical";
  const scored = (supplier.questionnaires ?? []).some((q) => q.riskScore != null);
  const reviewOverdue = isOverdue(supplier.nextReviewDate);
  const ended = supplier.status === "offboarded" || supplier.status === "rejected" || supplier.status === "inactive";

  // What the relationship still lacks, most pressing first. Each line names the tab that fixes it.
  const steps: NextStepItem[] = ended ? [] : [
    { done: !!supplier.riskBasis, label: "Record why the supplier is on this risk tier", hint: "Edit the supplier and fill in the risk rationale." },
    { done: !!supplier.internalOwner, label: "Name an internal owner" },
    { done: scored, label: "Score a due-diligence questionnaire", hint: "Assessments tab. The score keeps the linked register risk in step." },
    { done: live.length > 0, label: "Record the agreement in force", hint: "Contracts tab.", urgent: important && live.length === 0 && supplier.status === "active" },
    { done: coverage.gaps.length === 0, label: "Close the gaps in the agreement terms", hint: coverage.gaps.length ? `${coverage.gaps.length} term${coverage.gaps.length === 1 ? "" : "s"} this relationship needs ${coverage.gaps.length === 1 ? "is" : "are"} not in any agreement in force.` : undefined },
    { done: !!supplier.nextReviewDate && !reviewOverdue, label: supplier.nextReviewDate ? "Review the supplier" : "Set the next review date", hint: reviewOverdue ? `Due ${fmtDateOnly(supplier.nextReviewDate)}.` : undefined, urgent: reviewOverdue },
    ...(certs.length > 0 || important ? [{ done: certs.length > 0 && expiredCerts.length === 0, label: certs.length === 0 ? "Record the supplier's certifications" : "Renew the expired certifications", hint: expiredCerts.length ? `${expiredCerts.length} expired. Certifications tab.` : "Certifications tab." }] : []),
    ...(supplier.status === "prospective" ? [{ done: false, label: "Approve the supplier through the procurement gate", hint: "Needs a scored questionnaire or a documented risk basis." }] : []),
  ];
  const links: RelatedLink[] = [
    ...(supplier.tprmLinkedRisk ? [{ kindLabel: "Register risk", label: riskLabel(supplier.tprmLinkedRisk), href: KIND_HREFS.risk(supplier.tprmLinkedRisk.id) }] : []),
    ...activeAssets.map((a) => ({ kindLabel: "Asset delivered", label: `${a.name}${a.isEssentialFunction ? " (essential)" : ` (${a.criticality})`}`, href: KIND_HREFS.asset(a.id) })),
    ...actionLinks(actions),
  ];

  // The latest incident since which the supplier has not been reviewed (the list is newest first).
  const reviewSince = (incidents ?? []).find((i) => i.reviewDue) ?? null;

  return (
    <>
      <NextSteps items={steps} />
      {reviewSince && (
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 16, padding: "10px 12px", borderRadius: 3, background: "var(--c-warningBg)", border: "1px solid var(--c-warning)" }}>
          <AlertTriangle size={14} style={{ color: "var(--c-warningText)", flexShrink: 0 }} />
          <span style={{ ...text.body, color: "var(--c-warningText)", flex: 1, minWidth: 200 }}>
            Review this supplier: it has not been reviewed since {reviewSince.ref}.
            {supplier.nextReviewDate ? ` Next review is set for ${fmtDateOnly(supplier.nextReviewDate)}.` : " No next review date is set."}
          </span>
          {onRecordReview && <button type="button" onClick={onRecordReview} style={{ ...btnSecondary, padding: "6px 12px", fontSize: 12 }}>Record the review</button>}
        </div>
      )}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 20 }}>
        <div>
          <SectionTitle>The supplier</SectionTitle>
          <DetailRow label="Category">{supplier.category || dash}</DetailRow>
          <DetailRow label="Website">{safeHref(supplier.website) ? <a href={safeHref(supplier.website)} target="_blank" rel="noopener noreferrer" style={{ color: "var(--c-accentText)" }}>{supplier.website}</a> : supplier.website || dash}</DetailRow>
          <DetailRow label="Contact">{supplier.contactName || supplier.contactEmail ? <>{supplier.contactName}{supplier.contactName && supplier.contactEmail ? " · " : ""}{supplier.contactEmail && <a href={`mailto:${supplier.contactEmail}`} style={{ color: "var(--c-accentText)" }}>{supplier.contactEmail}</a>}</> : dash}</DetailRow>
          <DetailRow label="Internal owner">{supplier.internalOwner || dash}</DetailRow>
          <DetailRow label="Data processor">{supplier.isDataProcessor ? <>Yes — <PortalLink to="/gdpr/ropa" style={{ color: "var(--c-accentText)" }}>in the record of processing</PortalLink></> : "Not recorded as a processor"}</DetailRow>
        </div>
        <div>
          <SectionTitle>Risk and review</SectionTitle>
          <DetailRow label="Risk tier">{supplier.riskTier}</DetailRow>
          <DetailRow label="Status">{supplier.status.replace(/_/g, " ")}{supplier.approvedAt ? ` · approved ${fmtDate(supplier.approvedAt)}` : ""}</DetailRow>
          <DetailRow label="Latest assessment">{latest ? <button type="button" onClick={() => onOpenTab("assessments")} style={linkBtn}>{sentenceCase(latest.status)}{latest.riskScore != null ? ` · score ${latest.riskScore}` : ""}{latest.dataConfidence != null ? ` · confidence ${latest.dataConfidence}/5` : ""}</button> : <button type="button" onClick={() => onOpenTab("assessments")} style={linkBtn}>None yet — start one</button>}</DetailRow>
          <DetailRow label="Last review">{fmtDateOnly(supplier.lastReviewDate)}</DetailRow>
          <DetailRow label="Next review">{supplier.nextReviewDate ? <span style={{ color: isOverdue(supplier.nextReviewDate) ? "var(--c-dangerText)" : undefined }}>{fmtDateOnly(supplier.nextReviewDate)}{isOverdue(supplier.nextReviewDate) ? " (overdue)" : ""}</span> : dash}</DetailRow>
          <DetailRow label="Register risk">{supplier.tprmLinkedRisk ? <PortalLink to={`/risk-register?risk=${supplier.tprmLinkedRisk.id}`} style={{ color: "var(--c-accentText)" }}>{supplier.tprmLinkedRisk.displayId != null ? `RISK-${String(supplier.tprmLinkedRisk.displayId).padStart(3, "0")} ` : ""}{supplier.tprmLinkedRisk.title}</PortalLink> : dash}</DetailRow>
        </div>
      </div>

      <ConcentrationLine row={concentration} />

      {supplier.riskBasis && (
        <div style={{ ...text.hint, marginTop: 16, padding: "10px 12px", borderRadius: 3, background: "var(--c-panelAlt)" }}>
          <strong style={{ color: theme.color.muted }}>Risk rationale:</strong> {supplier.riskBasis}
        </div>
      )}
      {supplier.notes && (
        <div style={{ ...text.hint, marginTop: 10, padding: "10px 12px", borderRadius: 3, background: "var(--c-panelAlt)", whiteSpace: "pre-wrap" }}>
          <strong style={{ color: theme.color.muted }}>Notes:</strong> {supplier.notes}
        </div>
      )}

      <div style={{ marginTop: 20 }}>
        <SectionTitle>What is on file</SectionTitle>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 10, marginTop: 8 }}>
          {[
            { label: live.length === 1 ? "agreement in force" : "agreements in force", value: live.length, tab: "contracts" as PanelTab, warn: live.length === 0 },
            { label: coverage.gaps.length === 1 ? "term not in the agreements" : "terms not in the agreements", value: coverage.gaps.length, tab: "contracts" as PanelTab, warn: coverage.gaps.length > 0 },
            { label: certs.length === 1 ? "certification" : "certifications", value: certs.length, tab: "certifications" as PanelTab, warn: expiredCerts.length > 0 },
            { label: activeAssets.length === 1 ? "asset delivered" : "assets delivered", value: activeAssets.length, tab: "assets" as PanelTab, warn: false },
            { label: (supplier.scopeChanges?.length ?? 0) === 1 ? "scope change" : "scope changes", value: supplier.scopeChanges?.length ?? 0, tab: "scope" as PanelTab, warn: false },
          ].map((t) => (
            <button key={t.label} type="button" onClick={() => onOpenTab(t.tab)} style={{
              textAlign: "left", padding: "10px 12px", borderRadius: 3, cursor: "pointer",
              border: `1px solid ${t.warn ? "var(--c-warning)" : "var(--c-border)"}`,
              background: t.warn ? "var(--c-warningBg)" : "transparent",
            }}>
              <div style={{ ...text.stat, color: t.warn ? "var(--c-warningText)" : theme.color.heading }}>{t.value}</div>
              <div style={{ ...text.statLabel, fontWeight: 400, color: t.warn ? "var(--c-warningText)" : theme.color.muted }}>{t.label}</div>
            </button>
          ))}
        </div>
      </div>

      {incidents && (
        <div style={{ marginTop: 20 }}>
          <SectionTitle>Incidents ({incidents.length})</SectionTitle>
          {incidents.length === 0 ? (
            <div style={{ ...text.hint, color: theme.color.muted }}>No incident in the incident register names this supplier.</div>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table style={{ ...text.body, width: "100%", borderCollapse: "collapse", marginTop: 4 }}>
                <thead>
                  <tr style={{ textAlign: "left", color: text.label.color, fontSize: text.label.fontSize }}>
                    {["Incident", "Severity", "Status", "Detected"].map((h) => <th key={h} style={{ padding: "8px 10px", borderBottom: "1px solid var(--c-line)", fontWeight: 600, whiteSpace: "nowrap" }}>{h}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {incidents.map((i) => (
                    <tr key={i.id} style={{ borderBottom: "1px solid var(--c-border)" }}>
                      <td style={{ padding: "8px 10px", verticalAlign: "top" }}>
                        <PortalLink to={`/resilience/incidents?incident=${encodeURIComponent(i.id)}`} style={{ color: "var(--c-accentText)" }}>
                          <span style={{ ...text.ref, marginRight: 6 }}>{i.ref}</span>{i.title}
                        </PortalLink>
                        <div style={{ marginTop: 2 }}><NoticeVerdict incident={i} /></div>
                      </td>
                      <td style={{ padding: "8px 10px", verticalAlign: "top" }}><SeverityPill value={i.severity} /></td>
                      <td style={{ padding: "8px 10px", verticalAlign: "top" }}><StatusPill value={i.status} /></td>
                      <td style={{ padding: "8px 10px", verticalAlign: "top", whiteSpace: "nowrap", color: theme.color.muted }}>{fmtDate(i.detectedAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {activeAssets.length > 0 && (
        <div style={{ ...text.hint, marginTop: 18, color: theme.color.muted, lineHeight: 1.6 }}>
          Delivers{" "}
          {activeAssets.slice(0, 6).map((a, i) => (
            <span key={a.id}>{i > 0 ? ", " : ""}<PortalLink to={`/resilience/assets?asset=${a.id}`} style={{ color: "var(--c-accentText)" }}>{a.name}</PortalLink>{a.isEssentialFunction ? " (essential)" : a.criticality === "critical" ? " (critical)" : ""}</span>
          ))}
          {activeAssets.length > 6 ? ` and ${activeAssets.length - 6} more` : ""} in the <PortalLink to="/resilience/assets" style={{ color: "var(--c-accentText)" }}>asset register</PortalLink>.
        </div>
      )}

      <div style={{ marginTop: 18 }}>
        <EntityTagsRow entity={{ kind: "supplier", id: supplier.id }} />
        <RelatedRecords entity={{ kind: "supplier", id: supplier.id }} links={links} />
      </div>
    </>

  );
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return <div style={{ ...text.sectionTitle, marginBottom: 4 }}>{children}</div>;
}

const linkBtn: React.CSSProperties = { background: "none", border: "none", padding: 0, font: "inherit", color: "var(--c-accentText)", cursor: "pointer", textAlign: "left" };

// ── Contracts ─────────────────────────────────────────────────────────────────
// The agreements behind the relationship. What a date means today and which
// terms this relationship needs come from lib/supplierContracts.ts — the same
// rules the API, the attention list and the register document use.

const STANDING_COLORS: Record<ContractStanding, { bg: string; text: string }> = {
  draft:      { bg: "var(--c-faint)",     text: "var(--c-muted)" },
  active:     { bg: "var(--c-successBg)", text: "var(--c-successText)" },
  open_ended: { bg: "var(--c-successBg)", text: "var(--c-successText)" },
  expiring:   { bg: "var(--c-warningBg)", text: "var(--c-warningText)" },
  notice_due: { bg: "var(--c-warningBg)", text: "var(--c-warningText)" },
  expired:    { bg: "var(--c-dangerBg)",  text: "var(--c-dangerText)" },
  terminated: { bg: "var(--c-faint)",     text: "var(--c-muted)" },
};

const relationshipOf = (s: Supplier): ContractRelationship => ({
  isDataProcessor: s.isDataProcessor,
  ropaProcessor: !!s.ropaProcessor,
  riskTier: s.riskTier,
  deliversImportantService: (s.assets ?? []).some((a) => a.status === "active" && (a.criticality === "critical" || a.isEssentialFunction)),
});

function restoreCommitmentText(c: Contract): string {
  const hours = (n: number) => `${n} hour${n === 1 ? "" : "s"}`;
  if (c.restoreCommitted === true && c.restoreTimeHours != null) return `Within ${hours(c.restoreTimeHours)}${c.restorePointHours != null ? `, losing at most ${hours(c.restorePointHours)} of data` : ""}`;
  if (c.restoreCommitted === false) return "No restore commitment";
  return "Not answered";
}

function ContractsTab({ supplier, contracts, onChanged, canWrite, onOpenTab }: { supplier: Supplier; contracts: Contract[]; onChanged: () => void; canWrite: boolean; onOpenTab: (t: PanelTab) => void }) {
  const [editing, setEditing] = useState<{ id: string | null; form: ContractForm } | null>(null);
  // The agreement whose read-only view is open.
  const [viewing, setViewing] = useState<string | null>(null);
  const [error, setError]     = useState<string | null>(null);
  const rel = relationshipOf(supplier);
  const coverage = combinedTermCoverage(contracts, rel);
  const open = viewing ? contracts.find((c) => c.id === viewing) ?? null : null;

  async function remove(c: Contract) {
    if (!window.confirm(`Remove ${c.title} from the contract register?`)) return;
    try { await api.del(`/tprm/suppliers/${supplier.id}/contracts/${c.id}`); setViewing(null); onChanged(); }
    catch (err) { setError(apiErrorMessage(err, "Could not remove the agreement")); }
  }

  if (editing) return (
    <>
      <TabHead title={editing.id ? "Edit agreement" : "Record an agreement"} />
      <SupplierContractForm supplierId={supplier.id} contractId={editing.id} initial={editing.form} rel={rel}
        onSaved={(id, warning) => { setEditing(null); if (id) setViewing(id); setError(warning ?? null); onChanged(); }}
        onCancel={() => { setEditing(null); setError(null); }} />
    </>
  );

  if (open) {
    const t = contractTiming(open);
    const cover = termCoverage(open.terms ?? {}, rel);
    const answers = withLegacyTerms(open.terms);
    return (
      <>
        <BackLink label="All agreements" onClick={() => { setViewing(null); setError(null); }} />
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, marginBottom: 6 }}>
          <div style={{ minWidth: 0 }}>
            <div style={text.itemTitle}>{open.title}</div>
            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginTop: 4 }}>
              <Pill label={CONTRACT_STANDING_LABELS[t.standing]} colors={STANDING_COLORS[t.standing]} />
              <span style={text.hint}>{CONTRACT_KIND_LABELS[open.kind as ContractKind] ?? open.kind}{open.reference ? ` · ${open.reference}` : ""}</span>
            </div>
          </div>
          {canWrite && <EditButton onClick={() => { setError(null); setEditing({ id: open.id, form: toContractForm(open) }); }} />}
        </div>
        {t.noticeDeadline && (
          <div style={{ ...text.hint, marginTop: 8, color: t.standing === "notice_due" ? "var(--c-warningText)" : t.renewalLocked ? "var(--c-dangerText)" : theme.color.muted }}>
            {t.renewalLocked
              ? `The notice window closed on ${fmtDateOnly(t.noticeDeadline)} — it renews unless the supplier agrees otherwise.`
              : `Notice to leave at the end date must be given by ${fmtDateOnly(t.noticeDeadline)}${t.daysToNotice != null && t.daysToNotice >= 0 ? ` (${t.daysToNotice} days)` : ""}.`}
          </div>
        )}
        {/* Re-assessment before renewal: the questionnaire, the refreshed signals and the decision (lib/supplierRenewal.ts) */}
        <SupplierRenewalPanel supplier={supplier} contractId={open.id} canWrite={canWrite} onChanged={onChanged} onOpenTab={onOpenTab} />
        {/* The agreement's fields proposed from its signed copy, each confirmed by a member (lib/contractReading.ts) */}
        <ContractReadingPanel supplierId={supplier.id} contractId={open.id} canWrite={canWrite} onChanged={onChanged} />
        <DetailSection title="Dates and renewal">
          <DetailFields fields={[
            { label: "Status", value: open.status === "draft" ? "Draft — not signed" : open.status === "active" ? "Signed and in force" : open.status === "expired" ? "Expired" : open.status === "terminated" ? "Terminated" : open.status },
            { label: "Start date", value: open.startDate ? fmtDateOnly(open.startDate) : null },
            { label: "End date", value: open.endDate ? fmtDateOnly(open.endDate) : "No end date" },
            { label: "Renews automatically", value: open.autoRenew ? "Yes, unless notice is given" : "No" },
            { label: "Notice period", value: open.noticePeriodDays != null ? `${open.noticePeriodDays} days` : null },
            { label: "Next commercial review", value: open.reviewDate ? fmtDateOnly(open.reviewDate) : null },
          ]} />
        </DetailSection>
        <DetailSection title="Commercial and data">
          <DetailFields fields={[
            { label: "Agreement owner", value: open.owner },
            { label: "Annual value", value: open.annualValue != null ? `${open.currency ?? "GBP"} ${open.annualValue.toLocaleString()} a year` : null },
            { label: "Supplier must report an incident within", value: open.incidentNotificationHours != null ? `${open.incidentNotificationHours} hours` : null },
            { label: "Commits to restoring the service", value: restoreCommitmentText(open) },
            { label: "Data may be held in", value: (open.dataLocations ?? []).join(", ") },
            { label: "Signed copy", value: safeHref(open.documentUrl) ? <a href={safeHref(open.documentUrl)} target="_blank" rel="noopener noreferrer" style={{ color: "var(--c-accentText)" }}>Open the signed copy</a> : null },
            { label: "Notes", value: open.notes, wide: true, multiline: true },
          ]} />
        </DetailSection>
        <DetailSection title="Terms the relationship needs">
          <div style={{ ...text.hint, marginBottom: 8, color: cover.missing.length || cover.unanswered.length ? "var(--c-warningText)" : "var(--c-successText)" }}>
            {cover.present.length} of {cover.present.length + cover.missing.length + cover.unanswered.length} required terms recorded as in this agreement.
          </div>
          <ul style={{ margin: 0, paddingLeft: 18, ...text.hint, color: theme.color.text, lineHeight: 1.7 }}>
            {termsRequiredFor(rel).map((term) => (
              <li key={term.key} title={`${term.detail} (${term.basis})`}>
                {term.label}
                <span style={{ color: answers[term.key] === true ? "var(--c-successText)" : answers[term.key] === false ? "var(--c-warningText)" : theme.color.muted }}>
                  {answers[term.key] === true ? " — in the agreement" : answers[term.key] === false ? " — not in the agreement" : " — not answered"}
                </span>
              </li>
            ))}
          </ul>
        </DetailSection>
        {error && <div style={{ ...text.hint, color: "var(--c-dangerText)", marginBottom: 8 }}>{error}</div>}
        {canWrite && (
          <RecordActions>
            <button type="button" onClick={() => remove(open)} style={{ ...btnDanger, padding: "6px 12px", fontSize: 12 }}>Remove agreement</button>
          </RecordActions>
        )}
      </>
    );
  }

  return (
    <>
      <div style={{ marginBottom: 16, padding: "10px 14px", borderRadius: 3, background: "var(--c-panelAlt)", border: "1px solid var(--c-border)", fontSize: 12, color: theme.color.subtle, lineHeight: 1.5 }}>
        What is signed with this supplier, until when, and who has to decide whether to renew. Record which of the terms the relationship needs each agreement carries: a {supplier.riskTier}-tier{supplier.isDataProcessor ? ", data-processing" : ""} relationship needs {coverage.required.length} of them (ISO 27001 A.5.20–A.5.22{supplier.isDataProcessor ? ", GDPR Art. 28" : ""}). Expiring agreements and missing terms appear on the Resilience attention list.
      </div>

      {contracts.length > 0 && (
        <div style={{ marginBottom: 14, padding: "10px 12px", borderRadius: 3, border: `1px solid ${coverage.gaps.length || !coverage.inForce ? "var(--c-warning)" : "var(--c-border)"}`, background: coverage.gaps.length || !coverage.inForce ? "var(--c-warningBg)" : "transparent" }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: coverage.gaps.length || !coverage.inForce ? "var(--c-warningText)" : theme.color.heading }}>
            {coverage.inForce === 0
              ? `No agreement in force: none of the ${coverage.required.length} terms this relationship needs is covered until one is recorded or renewed.`
              : coverage.gaps.length === 0
              ? `Every term this relationship needs is in an agreement in force (${coverage.required.length} of ${coverage.required.length}).`
              : `${coverage.required.length - coverage.gaps.length} of ${coverage.required.length} terms this relationship needs are in an agreement in force.`}
          </div>
          {coverage.gaps.length > 0 && (
            <ul style={{ margin: "6px 0 0", paddingLeft: 18, fontSize: 12, color: "var(--c-warningText)", lineHeight: 1.6 }}>
              {coverage.gaps.map((t) => (
                <li key={t.key} title={`${t.detail} (${t.basis})`}>{t.label}{coverage.unanswered.some((u) => u.key === t.key) ? " — not answered on any agreement" : " — not in the agreement"}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      <SupplierRenewalPanel supplier={supplier} canWrite={canWrite} onChanged={onChanged} onOpenTab={onOpenTab} />

      {contracts.length === 0 ? (
        <div style={{ fontSize: 13, color: theme.color.muted, marginBottom: 14 }}>No agreements recorded for this supplier.</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 14 }}>
          {contracts.map((c) => {
            const t = contractTiming(c);
            const colors = STANDING_COLORS[t.standing];
            const cover = termCoverage(c.terms ?? {}, rel);
            return (
              <button key={c.id} type="button" onClick={() => setViewing(c.id)} title={`Open ${c.title}`} style={{ ...rowBtn, padding: "10px 12px", border: `1px solid ${t.standing === "expired" ? "var(--c-danger)" : "var(--c-border)"}` }}>
                <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 10 }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: theme.color.text }}>
                      {c.title}
                      <span style={{ fontWeight: 400, color: theme.color.muted }}> · {CONTRACT_KIND_LABELS[c.kind as ContractKind] ?? c.kind}{c.reference ? ` · ${c.reference}` : ""}</span>
                      <span style={{ marginLeft: 8 }}><Pill label={CONTRACT_STANDING_LABELS[t.standing]} colors={colors} /></span>
                    </div>
                    <div style={{ fontSize: 12, color: theme.color.muted, marginTop: 4 }}>
                      {c.startDate ? `From ${fmtDateOnly(c.startDate)} ` : ""}
                      {c.endDate ? `to ${fmtDateOnly(c.endDate)}` : "with no end date"}
                      {c.autoRenew ? " · auto-renews" : ""}
                      {t.noticeDeadline && !t.renewalLocked && t.daysToNotice != null && t.daysToNotice >= 0 ? ` · notice by ${fmtDateOnly(t.noticeDeadline)}` : ""}
                      {c.owner ? ` · owner ${c.owner}` : ""}
                    </div>
                    <div style={{ fontSize: 12, marginTop: 3, color: cover.missing.length || cover.unanswered.length ? "var(--c-warningText)" : "var(--c-successText)" }}>
                      {cover.present.length} of {cover.present.length + cover.missing.length + cover.unanswered.length} required terms in this agreement
                    </div>
                  </div>
                  <ChevronRight size={15} color="var(--c-muted)" style={{ flexShrink: 0, marginTop: 2 }} />
                </div>
              </button>
            );
          })}
        </div>
      )}

      {error && <div style={{ ...text.hint, color: "var(--c-dangerText)", marginBottom: 8 }}>{error}</div>}
      {canWrite && (
        <button type="button" onClick={() => { setError(null); setEditing({ id: null, form: { ...emptyContract, owner: supplier.internalOwner ?? "" } }); }} style={{ ...btnPrimary, fontSize: 13 }}>
          <Plus size={14} style={{ verticalAlign: -2, marginRight: 6 }} />Record an agreement
        </button>
      )}
    </>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export function TprmPage() {
  const [addonEnabled,  setAddonEnabled]  = useState<boolean | null>(null);
  const [suppliers,     setSuppliers]     = useState<Supplier[]>([]);
  const [loading,       setLoading]       = useState(true);
  const [activeTab,     setActiveTab]     = useState<Tab>("suppliers");
  const [showAddForm,   setShowAddForm]   = useState(false);
  const [panelSupplier, setPanelSupplier] = useState<Supplier | null>(null);
  const [panelTab,      setPanelTab]      = useState<PanelTab>("overview");
  // A questionnaire to open straight into its view on the Assessments tab.
  const [panelQuestionnaireId, setPanelQuestionnaireId] = useState<string | null>(null);
  // ?supplier=<id> opens a supplier straight away — the asset register, the
  // attention list and the resilience pages link to a supplier this way.
  const [searchParams, setSearchParams]   = useSearchParams();
  const deepLinkId = searchParams.get("supplier");
  const [saving,        setSaving]        = useState(false);
  const [error,         setError]         = useState<string | null>(null);
  // What an offboarding left behind in the asset register (PUT /tprm/suppliers/:id reports dependentAssets).
  const [notice,        setNotice]        = useState<string | null>(null);
  const [filterTier,    setFilterTier]    = useState("all");
  const tagData = useEntityTagData();
  const [tagFilter, setTagFilter] = useTagFilter();
  const [filterStatus,  setFilterStatus]  = useState("all");
  const { catalogue, loading: catalogueLoading, reload: reloadCatalogue } = useQuestionnaireCatalogue(addonEnabled === true);
  const { user } = useCurrentUser();
  // Mirrors tprm:write on the reminder route; the server enforces it.
  const canWrite = !!user?.permissions?.includes("tprm:write");
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("tier");
  // The tile that filters the register: high / critical, review overdue, single point of failure.
  const [tileFilter, setTileFilter] = useState<"" | "highcrit" | "review" | "spof">("");
  const [concentration, setConcentration] = useState<SupplierConcentrationRow[] | null>(null);
  const [outstanding, setOutstanding] = useState<{ afterDays: number; questionnaires: OutstandingQuestionnaire[]; autoChase?: AutoChaseSummary } | null>(null);
  // Each supplier's standing from its signals (GET /tprm/standings): a chip on the register, never a score.
  const [standings, setStandings] = useState<Record<string, { standing: SupplierStanding; concerns: number }>>({});
  // Connecting the vendor ratings account is integrations:admin on the server.
  const canAdminIntegrations = !!user?.permissions?.includes("integrations:admin");

  const loadOutstanding = useCallback(() => {
    api.get<{ afterDays: number; questionnaires: OutstandingQuestionnaire[]; autoChase?: AutoChaseSummary }>("/tprm/questionnaires/outstanding").then(setOutstanding).catch(() => setOutstanding(null));
  }, []);

  const loadSuppliers = useCallback(async () => {
    try {
      const [addonData, suppData] = await Promise.all([
        api.get<{ addons: { tprm: boolean } }>("/organizations/addons").catch(() => ({ addons: { tprm: false } })),
        api.get<{ suppliers: Supplier[] }>("/tprm/suppliers").catch(() => null),
      ]);
      setAddonEnabled(addonData.addons.tprm);
      if (suppData) setSuppliers(suppData.suppliers);
      if (addonData.addons.tprm) {
        // Concentration and the questionnaire chase: read alongside the register, never block it.
        api.get<{ concentration: SupplierConcentrationRow[] }>("/tprm/concentration").then((d) => setConcentration(d.concentration)).catch(() => setConcentration(null));
        loadOutstanding();
        api.get<{ standings: Record<string, { standing: SupplierStanding; concerns: number }> }>("/tprm/standings").then((d) => setStandings(d.standings)).catch(() => setStandings({}));
      }
    } catch {
      setAddonEnabled(false);
    } finally {
      setLoading(false);
    }
  }, [loadOutstanding]);

  useEffect(() => { loadSuppliers(); }, [loadSuppliers]);

  // Open the linked supplier once the register is in hand; an id that names no
  // supplier of this organisation simply leaves the list as it is.
  useEffect(() => {
    if (!deepLinkId || panelSupplier) return;
    const match = suppliers.find((s) => s.id === deepLinkId);
    // &panel=<tab> opens the supplier on the part the link is about, and
    // &questionnaire=<id> on one questionnaire (an overdue one from the attention list).
    const panel = searchParams.get("panel");
    const PANEL_TABS: PanelTab[] = ["overview", "assessments", "assurance", "compromise", "contracts", "scope", "monitoring", "certifications", "assets"];
    const questionnaireId = searchParams.get("questionnaire");
    if (match) {
      setPanelTab(questionnaireId ? "assessments" : PANEL_TABS.includes(panel as PanelTab) ? panel as PanelTab : "overview");
      setPanelQuestionnaireId(questionnaireId);
      setPanelSupplier(match);
    }
  }, [deepLinkId, suppliers, panelSupplier]); // eslint-disable-line react-hooks/exhaustive-deps

  // ?new=1 (the search box's "Add a supplier") opens the add form on the register.
  useNewFromLink(canWrite && addonEnabled === true, () => { setActiveTab("suppliers"); setShowAddForm(true); });

  // ?tab=contracts | monitoring | questionnaires: the calendar, the chase and the templates are linkable too.
  const tabParam = searchParams.get("tab");
  useEffect(() => {
    if (tabParam === "contracts" || tabParam === "monitoring" || tabParam === "questionnaires" || tabParam === "suppliers") setActiveTab(tabParam);
  }, [tabParam]);

  function openSupplier(s: Supplier, tab: PanelTab = "overview", questionnaireId: string | null = null) {
    setPanelTab(tab);
    setPanelQuestionnaireId(questionnaireId);
    setPanelSupplier(s);
  }

  function closeSupplier() {
    setPanelSupplier(null);
    if (searchParams.has("supplier")) {
      const next = new URLSearchParams(searchParams);
      next.delete("supplier");
      next.delete("panel");
      next.delete("questionnaire");
      setSearchParams(next, { replace: true });
    }
  }

  async function handleAdd(data: Partial<Supplier>) {
    setSaving(true);
    setError(null);
    try {
      await api.post("/tprm/suppliers", data);
      setShowAddForm(false);
      await loadSuppliers();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Failed to add supplier");
    } finally {
      setSaving(false);
    }
  }

  // Saved from the supplier panel's Overview Edit; the panel returns to its view on success.
  async function handleEdit(id: string, data: Partial<Supplier>): Promise<string | null> {
    setSaving(true);
    setError(null);
    try {
      const r = await api.put<{ supplier: Supplier; dependentAssets?: DependentAssets }>(`/tprm/suppliers/${id}`, data);
      setNotice(r.dependentAssets ? dependentAssetsNotice(r.supplier.name, r.supplier.status, r.dependentAssets) : null);
      await loadSuppliers();
      return null;
    } catch (e: unknown) {
      return e instanceof Error ? e.message : "Failed to save";
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(id: string, name: string) {
    if (!confirm(`Remove ${name} from your supplier register?`)) return;
    try {
      await api.del(`/tprm/suppliers/${id}`);
      // Drop it from the list first: a ?supplier=<id> link would otherwise reopen the panel on
      // the deleted row before the reload lands (DOC-27).
      setSuppliers((prev) => prev.filter((s) => s.id !== id));
      closeSupplier();
      await loadSuppliers();
    } catch (err) {
      // Assets still delivered by the supplier: the API refuses until the member accepts unlinking them.
      const refused = err instanceof ApiError && err.status === 409 && typeof err.payload === "object" && err.payload && Array.isArray((err.payload as { assets?: unknown }).assets)
        ? (err.payload as { assets: Array<{ name: string; criticality: string }> }).assets : null;
      if (!refused) { closeSupplier(); setError(apiErrorMessage(err, `${name} could not be removed.`)); return; }
      const names = refused.map((a) => `${a.name} (${a.criticality})`).join(", ");
      if (!confirm(`${refused.length} asset${refused.length === 1 ? " is" : "s are"} delivered by ${name}: ${names}.\n\nDelete the supplier anyway? Each asset keeps a dated note and a "supplier-removed" tag and stays on the attention list until you name a replacement supplier or retire it.`)) return;
      try {
        await api.del(`/tprm/suppliers/${id}`, { unlinkAssets: "true" });
        setSuppliers((prev) => prev.filter((s) => s.id !== id));
        closeSupplier();
        await loadSuppliers();
      } catch (err2) {
        closeSupplier();
        setError(apiErrorMessage(err2, `${name} could not be removed.`));
      }
    }
  }

  const [togglingDp, setTogglingDp] = useState<string | null>(null);
  async function handleToggleDataProcessor(id: string, current: boolean) {
    setTogglingDp(id);
    try {
      await api.patch(`/tprm/suppliers/${id}/data-processor`, { isDataProcessor: !current });
      setSuppliers((prev) => prev.map((s) => s.id === id ? { ...s, isDataProcessor: !current } : s));
    } catch {
      setError("Failed to update data processor flag.");
    } finally {
      setTogglingDp(null);
    }
  }

  const concentrationById = new Map((concentration ?? []).map((c) => [c.supplierId, c]));
  const spofIds = new Set((concentration ?? []).filter((c) => c.singlePointOfFailure === "yes").map((c) => c.supplierId));
  const reviewOverdue = (s: Supplier) => !!s.nextReviewDate && isOverdue(s.nextReviewDate) && s.status !== "offboarded" && s.status !== "rejected";

  const sortOptions: SortOption<Supplier>[] = [
    { key: "tier", label: "Risk tier", compare: (a, b) => by.rank<Supplier>((s) => s.riskTier, ["critical", "high", "medium", "low"])(a, b) || a.name.localeCompare(b.name) },
    { key: "name", label: "Name", compare: by.text<Supplier>((s) => s.name) },
    { key: "review", label: "Next review", compare: by.date<Supplier>((s) => s.nextReviewDate, "asc") },
    { key: "status", label: "Status", compare: (a, b) => by.rank<Supplier>((s) => s.status, STATUSES)(a, b) || a.name.localeCompare(b.name) },
    { key: "concentration", label: "Concentration", compare: by.number<Supplier>((s) => { const c = concentrationById.get(s.id); return c ? (c.singlePointOfFailure === "yes" ? 1000 : 0) + c.essentialAssets * 10 + c.importantAssets.length : 0; }) },
  ];

  const filtered = suppliers.filter((s) => {
    if (!matchesEntityTagFilter(tagData, entityRef({ kind: "supplier", id: s.id }), tagFilter)) return false;
    if (filterTier   !== "all" && s.riskTier !== filterTier)   return false;
    if (filterStatus !== "all" && s.status   !== filterStatus) return false;
    if (tileFilter === "highcrit" && s.riskTier !== "high" && s.riskTier !== "critical") return false;
    if (tileFilter === "review" && !reviewOverdue(s)) return false;
    if (tileFilter === "spof" && !spofIds.has(s.id)) return false;
    return matchesSearch(search, s.name, s.category, s.internalOwner, s.contactName, s.contactEmail);
  }).sort(sortOptions.find((o) => o.key === sort)?.compare ?? (() => 0));

  function exportSuppliers() {
    downloadCsv<Supplier>("suppliers", [
      { header: "Supplier", value: (s) => s.name },
      { header: "Category", value: (s) => s.category },
      { header: "Risk tier", value: (s) => tierLabel(s.riskTier) },
      { header: "Status", value: (s) => statusLabel(s.status) },
      { header: "Standing (signals, not a rating)", value: (s) => (standings[s.id] ? STANDING_LABELS[standings[s.id].standing] : "") },
      { header: "Internal owner", value: (s) => s.internalOwner ?? "" },
      { header: "Contact", value: (s) => s.contactName ?? "" },
      { header: "Contact email", value: (s) => s.contactEmail ?? "" },
      { header: "Data processor", value: (s) => s.isDataProcessor },
      { header: "Last review", value: (s) => (s.lastReviewDate ? s.lastReviewDate.slice(0, 10) : "") },
      { header: "Next review", value: (s) => (s.nextReviewDate ? s.nextReviewDate.slice(0, 10) : "") },
      { header: "Latest questionnaire", value: (s) => (s.questionnaires?.[0]?.status ? sentenceCase(s.questionnaires[0].status) : "") },
      { header: "Latest score", value: (s) => s.questionnaires?.[0]?.riskScore ?? "" },
      { header: "Agreements", value: (s) => contractLabel(s)?.text ?? "" },
      { header: "Assets delivered", value: (s) => (s.assets ?? []).filter((a) => a.status === "active").length },
      { header: "Single point of failure", value: (s) => { const c = concentrationById.get(s.id); return c ? (c.singlePointOfFailure === "yes" ? "Yes" : c.singlePointOfFailure === "unknown" ? "No alternative recorded" : "No") : ""; } },
    ], filtered);
  }

  const highCritCount  = suppliers.filter((s) => s.riskTier === "critical" || s.riskTier === "high").length;
  const criticalCount  = suppliers.filter((s) => s.riskTier === "critical").length;
  const reviewDueCount = suppliers.filter(reviewOverdue).length;
  const notice30       = noticeDeadlinesWithin(suppliers, 30);
  const noticeOverdue  = contractCalendar(suppliers).filter((r) => r.group === "overdue").length;
  const awaitingReturn = suppliers.reduce((n, s) => n + (s.questionnaires ?? []).filter((q) => q.status === "sent").length, 0);
  const chaseCount     = outstanding?.questionnaires.length ?? 0;
  const unassessedSpof = (concentration ?? []).filter((c) => c.singlePointOfFailure === "unknown").length;
  const recentScopeChanges = suppliers.flatMap((s) =>
    (s.scopeChanges ?? []).map((sc) => ({ ...sc, supplierName: s.name, supplier: s }))
  ).filter((sc) => new Date(sc.changedAt) >= new Date(Date.now() - 30 * 24 * 60 * 60 * 1000))
   .sort((a, b) => new Date(b.changedAt).getTime() - new Date(a.changedAt).getTime());

  // If a panel is open and suppliers reload, sync the panel supplier
  const syncedPanelSupplier = panelSupplier
    ? (suppliers.find((s) => s.id === panelSupplier.id) ?? panelSupplier)
    : null;

  if (!loading && addonEnabled === false) {
    return (
      <div className="page-pad">
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
          <Building2 size={22} color="var(--c-accentText)" />
          <h1 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: theme.color.heading, letterSpacing: "-0.3px" }}>
            Suppliers
          </h1>
        </div>
        <p style={{ margin: "0 0 24px", fontSize: 13, color: theme.color.subtle }}>
          Manage supplier relationships, send risk questionnaires, and track ongoing compliance posture of third parties with access to your systems or data.
        </p>
        <PlaybookBar ids={["third-party-risk"]} />
        <div style={{ ...card, padding: "28px", border: `1px solid ${theme.color.border}`, textAlign: "center" }}>
          <Lock size={36} style={{ marginBottom: 14, color: theme.color.subtle }} />
          <div style={{ fontSize: 16, fontWeight: 700, color: theme.color.heading, marginBottom: 8 }}>
            TPRM Add-on Not Enabled
          </div>
          <div style={{ fontSize: 13, color: theme.color.subtle, lineHeight: 1.6, maxWidth: 380, margin: "0 auto 20px" }}>
            Third-party risk management is an optional add-on at <strong style={{ color: theme.color.text }}>{gbpMo(TPRM_ADDON_PENCE)}</strong>.
            Ask your consultant to activate it for your organisation.
          </div>
          <PortalLink to="/my-services" style={{
            display: "inline-block", padding: "9px 20px", borderRadius: theme.radius.sm,
            background: theme.color.accent, color: "#fff", fontSize: 13, fontWeight: 600, textDecoration: "none",
          }}>
            View My Services
          </PortalLink>
        </div>
      </div>
    );
  }

  return (
    <div className="page-pad">
      {syncedPanelSupplier && (
        <SupplierPanel
          key={syncedPanelSupplier.id}
          supplier={syncedPanelSupplier}
          catalogue={catalogue}
          initialTab={panelTab}
          onClose={closeSupplier}
          onUpdate={loadSuppliers}
          onManageQuestionnaires={() => { closeSupplier(); setActiveTab("questionnaires"); }}
          concentration={concentrationById.get(syncedPanelSupplier.id)}
          canWrite={canWrite}
          initialQuestionnaireId={panelQuestionnaireId}
          onSaveSupplier={(data) => handleEdit(syncedPanelSupplier.id, data)}
          savingSupplier={saving}
          onDelete={() => handleDelete(syncedPanelSupplier.id, syncedPanelSupplier.name)}
          onToggleDataProcessor={() => handleToggleDataProcessor(syncedPanelSupplier.id, syncedPanelSupplier.isDataProcessor)}
          togglingDataProcessor={togglingDp === syncedPanelSupplier.id}
        />
      )}

      {/* Title */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
        <Building2 size={22} color="var(--c-accentText)" />
        <h1 style={{ ...pageHeading, margin: 0 }}>
          Third-party risk management
        </h1>
      </div>
      <p style={{ margin: "0 0 24px", fontSize: 13, color: theme.color.subtle }}>
        Manage supplier relationships, run risk assessments, track scope changes, and monitor ongoing third-party compliance posture.
      </p>
      <PlaybookBar ids={["third-party-risk"]} />
      {/* Below the playbook link, never over it (AUD-42). */}
      <div style={{ marginBottom: 20 }}><PolicySaysChip keys={["supplier.questionnaire_chase_days"]} /></div>

      {/* Summary tiles */}
      {!loading && (
        <SummaryTiles tiles={[
          { key: "total", label: "Suppliers", value: suppliers.length, hint: tileFilter ? "Show all" : `${recentScopeChanges.length} scope change${recentScopeChanges.length === 1 ? "" : "s"} in 30 days`, active: false, onClick: () => { setActiveTab("suppliers"); setTileFilter(""); setFilterTier("all"); setFilterStatus("all"); setSearch(""); } },
          { key: "highcrit", label: "Critical or high risk", value: highCritCount, hint: `${criticalCount} critical`, tone: highCritCount ? "danger" : "neutral", active: tileFilter === "highcrit", onClick: () => { setActiveTab("suppliers"); setTileFilter(tileFilter === "highcrit" ? "" : "highcrit"); } },
          { key: "review", label: "Reviews overdue", value: reviewDueCount, tone: reviewDueCount ? "warning" : "success", active: tileFilter === "review", onClick: () => { setActiveTab("suppliers"); setTileFilter(tileFilter === "review" ? "" : "review"); } },
          // The figure counts what is overdue too, so a red tile never reads "0" (AUD-42).
          { key: "notice", label: noticeOverdue ? "Notice deadlines overdue or in 30 days" : "Notice deadlines in 30 days", value: notice30 + noticeOverdue, hint: noticeOverdue ? `${noticeOverdue} overdue` : "Renewal and notice calendar", tone: noticeOverdue ? "danger" : notice30 ? "warning" : "neutral", active: activeTab === "contracts", onClick: () => setActiveTab(activeTab === "contracts" ? "suppliers" : "contracts") },
          { key: "questionnaires", label: "Questionnaires awaiting return", value: awaitingReturn, hint: chaseCount ? `${chaseCount} waiting ${outstanding?.afterDays ?? 14}+ days` : "None overdue", tone: chaseCount ? "warning" : "neutral", active: activeTab === "monitoring", onClick: () => setActiveTab(activeTab === "monitoring" ? "suppliers" : "monitoring") },
          { key: "spof", label: "Single points of failure", value: concentration ? spofIds.size : "—", hint: unassessedSpof ? `${unassessedSpof} with no alternative recorded` : "Essential functions resting on one supplier", tone: spofIds.size ? "danger" : unassessedSpof ? "warning" : "neutral", active: tileFilter === "spof", onClick: () => { setActiveTab("suppliers"); setTileFilter(tileFilter === "spof" ? "" : "spof"); } },
        ]} />
      )}

      {/* Tabs */}
      <IndexTabs<Tab>
        items={[
          { id: "suppliers", label: "Suppliers" },
          { id: "contracts", label: "Contracts" },
          { id: "questionnaires", label: "Questionnaires" },
          { id: "monitoring", label: "Monitoring" },
        ]}
        value={activeTab}
        onChange={setActiveTab}
        style={{ marginBottom: 20 }}
      />

      {error && (
        <div style={{ ...card, marginBottom: 16, padding: "10px 14px", background: "var(--c-dangerBg)", border: "1px solid var(--c-danger)", color: "var(--c-dangerText)", fontSize: 13 }}>
          {error}
        </div>
      )}
      {notice && (
        <div style={{ ...card, marginBottom: 16, padding: "10px 14px", background: "var(--c-warningBg)", color: "var(--c-warningText)", fontSize: 13, display: "flex", gap: 12, alignItems: "flex-start", justifyContent: "space-between" }}>
          <span>{notice} <PortalLink to="/resilience/assets" style={{ color: "inherit", fontWeight: 600 }}>Open the asset register</PortalLink></span>
          <button onClick={() => setNotice(null)} aria-label="Dismiss" style={{ background: "none", border: "none", color: "inherit", cursor: "pointer", padding: 0, flexShrink: 0 }}><X size={14} /></button>
        </div>
      )}

      {/* ── Suppliers tab ── */}
      {activeTab === "suppliers" && (
        <>
          <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap", alignItems: "center" }}>
            <div style={{ display: "flex", gap: 5 }}>
              {["all", ...RISK_TIERS].map((t) => (
                <button key={t} onClick={() => setFilterTier(t)} style={{
                  padding: "5px 12px", borderRadius: 3, fontSize: 12,
                  fontWeight: filterTier === t ? 600 : 400,
                  border: filterTier === t ? `2px solid ${theme.color.accent}` : "1px solid var(--c-border)",
                  background: filterTier === t ? "var(--c-accentBg)" : "var(--c-panel)",
                  color: filterTier === t ? "var(--c-accentText)" : theme.color.muted, cursor: "pointer",
                }}>
                  {t === "all" ? "All tiers" : tierLabel(t)}
                </button>
              ))}
            </div>
            <EntityTagFilters value={tagFilter} onChange={setTagFilter} />
            <div style={{ flex: 1 }} />
            {canWrite && (
              <button onClick={() => setShowAddForm(true)} style={{ ...btnPrimary, display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
                <Plus size={14} /> Add supplier
              </button>
            )}
          </div>

          {/* Status */}
          <div style={{ display: "flex", gap: 5, marginBottom: 12, flexWrap: "wrap" }}>
            {["all", ...STATUSES].map((st) => {
              const n = st === "all" ? suppliers.length : suppliers.filter((s) => s.status === st).length;
              if (st !== "all" && n === 0 && filterStatus !== st) return null;
              return (
                <button key={st} type="button" onClick={() => setFilterStatus(st)} style={{
                  padding: "4px 11px", borderRadius: 3, fontSize: 12,
                  fontWeight: filterStatus === st ? 600 : 400,
                  border: filterStatus === st ? `2px solid ${theme.color.accent}` : "1px solid var(--c-border)",
                  background: filterStatus === st ? "var(--c-accentBg)" : "var(--c-panel)",
                  color: filterStatus === st ? "var(--c-accentText)" : theme.color.muted, cursor: "pointer",
                }}>
                  {st === "all" ? "All statuses" : statusLabel(st)} ({n})
                </button>
              );
            })}
          </div>

          <RegisterToolbar<Supplier>
            search={search}
            onSearch={setSearch}
            placeholder="Search name, category or owner"
            sortOptions={sortOptions}
            sort={sort}
            onSort={setSort}
            count={{ shown: filtered.length, total: suppliers.length }}
            onExport={filtered.length ? exportSuppliers : undefined}
          >
            {tileFilter && (
              <button type="button" onClick={() => setTileFilter("")} style={{ padding: "6px 10px", fontSize: text.hint.fontSize, borderRadius: 3, border: "1px solid var(--c-border)", background: "var(--c-panel)", color: theme.color.text, cursor: "pointer" }}>
                {tileFilter === "highcrit" ? "Critical or high" : tileFilter === "review" ? "Review overdue" : "Single points of failure"} <X size={11} style={{ verticalAlign: "middle" }} />
              </button>
            )}
          </RegisterToolbar>

          <ConcentrationCard rows={concentration} onOpen={(id) => { const s = suppliers.find((x) => x.id === id); if (s) openSupplier(s); }} />

          {showAddForm && canWrite && (
            <div style={{ ...card, marginBottom: 20 }}>
              <h3 style={{ margin: "0 0 16px", fontSize: 14, fontWeight: 600, color: theme.color.heading }}>New Supplier</h3>
              <SupplierForm onSave={handleAdd} onCancel={() => setShowAddForm(false)} saving={saving} />
            </div>
          )}

          {loading ? (
            <div style={{ ...card, color: theme.color.subtle, fontSize: 13 }}>Loading suppliers…</div>
          ) : filtered.length === 0 ? (
            <div style={{ ...emptyFrame, padding: "40px 28px" }}>
              <Building2 size={36} color="var(--c-muted)" style={{ marginBottom: 12 }} />
              <div style={{ fontSize: 15, fontWeight: 600, color: theme.color.heading, marginBottom: 6 }}>
                {suppliers.length === 0 ? "No suppliers yet" : "No suppliers match this filter"}
              </div>
              {suppliers.length === 0 && (
                <p style={{ fontSize: 13, color: theme.color.muted, maxWidth: 460, margin: "0 auto 20px", lineHeight: 1.6 }}>
                  Add your first supplier to start tracking third-party risk across your supply chain.
                </p>
              )}
            </div>
          ) : (
            <div style={{ ...card, padding: 0, overflow: "hidden" }}>
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                  <thead>
                    <tr style={{ borderBottom: "1px solid var(--c-line)", background: theme.color.panelAlt }}>
                      {["Supplier", "Owner", "Category", "Risk tier", "Status", "Next review", "Latest assessment"].map((h) => (
                        <th key={h} style={{ padding: "10px 14px", textAlign: "left", fontWeight: 600, fontSize: 12, color: theme.color.muted, whiteSpace: "nowrap" }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((s, i) => (
                        <tr key={s.id} onClick={() => openSupplier(s)} style={{ cursor: "pointer", borderBottom: i < filtered.length - 1 ? `1px solid ${theme.color.border}` : "none" }}>
                          <td style={{ padding: "12px 14px", maxWidth: 220 }}>
                            <button type="button" onClick={(e) => { e.stopPropagation(); openSupplier(s); }} title={`Open ${s.name}`}
                              style={{ display: "block", maxWidth: "100%", padding: 0, border: "none", background: "none", font: "inherit", fontWeight: 600, color: theme.color.heading, cursor: "pointer", textAlign: "left", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                              {s.name}
                            </button>
                            {s.contactEmail && <div style={{ fontSize: 11, color: theme.color.muted, marginTop: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s.contactEmail}</div>}
                            {(s.scopeChanges?.length ?? 0) > 0 && (
                              <div style={{ fontSize: 10, color: "var(--c-accentText)", marginTop: 2, display: "flex", alignItems: "center", gap: 3 }}>
                                <GitBranch size={10} /> {s.scopeChanges.length} scope change{s.scopeChanges.length > 1 ? "s" : ""}
                              </div>
                            )}
                            {contractLabel(s) && (
                              <div style={{ fontSize: 10, color: contractLabel(s)!.warn ? "var(--c-warningText)" : theme.color.muted, marginTop: 2, display: "flex", alignItems: "center", gap: 3 }} title="Agreements recorded for this supplier">
                                <FileText size={10} /> {contractLabel(s)!.text}
                              </div>
                            )}
                            {deliversLabel(s.assets) && (
                              <div style={{ fontSize: 10, color: theme.color.muted, marginTop: 2, display: "flex", alignItems: "center", gap: 3 }} title="Assets in the resilience register delivered by this supplier">
                                <Boxes size={10} /> {deliversLabel(s.assets)}
                              </div>
                            )}
                            {s.isDataProcessor && <div style={{ fontSize: 10, color: "var(--c-accentText)", marginTop: 2 }}>Data processor</div>}
                            <EntityTagsRow entity={{ kind: "supplier", id: s.id }} readOnly style={{ marginTop: 4, marginBottom: 0 }} />
                          </td>
                          <td style={{ padding: "12px 14px", color: theme.color.muted, fontSize: 12, whiteSpace: "nowrap" }}>
                            {s.internalOwner ?? <span style={{ color: theme.color.faint }}>—</span>}
                          </td>
                          <td style={{ padding: "12px 14px", color: theme.color.muted, whiteSpace: "nowrap" }}>{s.category}</td>
                          <td style={{ padding: "12px 14px", whiteSpace: "nowrap" }}>
                            <Pill label={s.riskTier} colors={TIER_COLORS[s.riskTier] ?? { bg: "var(--c-faint)", text: "var(--c-muted)" }} />
                          </td>
                          <td style={{ padding: "12px 14px", whiteSpace: "nowrap" }}>
                            <Pill label={s.status} colors={STATUS_COLORS[s.status] ?? { bg: "var(--c-faint)", text: "var(--c-muted)" }} />
                            {standings[s.id] && (
                              <div style={{ marginTop: 4 }}>
                                <StandingChip standing={standings[s.id].standing} concerns={standings[s.id].concerns} />
                              </div>
                            )}
                          </td>
                          <td style={{ padding: "12px 14px", whiteSpace: "nowrap", fontSize: 12 }}>
                            {s.nextReviewDate ? (
                              <span style={{ color: isOverdue(s.nextReviewDate) ? "var(--c-dangerText)" : theme.color.muted }}>
                                {fmtDateOnly(s.nextReviewDate)}
                              </span>
                            ) : "—"}
                          </td>
                          <td style={{ padding: "12px 14px", whiteSpace: "nowrap", fontSize: 12, color: theme.color.muted }}>
                            {s.questionnaires?.[0]
                              ? <>{sentenceCase(s.questionnaires[0].status)}{s.questionnaires[0].riskScore != null ? ` · ${s.questionnaires[0].riskScore}%` : ""}</>
                              : <span style={{ color: theme.color.faint }}>None</span>}
                          </td>
                        </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {/* ── Contracts: renewal and notice calendar ── */}
      {activeTab === "contracts" && (
        loading
          ? <div style={{ ...card, ...text.body, color: theme.color.subtle }}>Loading…</div>
          : <ContractsCalendar suppliers={suppliers} onOpen={(id) => { const s = suppliers.find((x) => x.id === id); if (s) openSupplier(s, "contracts"); }} />
      )}

      {/* ── Questionnaires tab ── */}
      {activeTab === "questionnaires" && (
        <QuestionnaireTemplatesTab
          catalogue={catalogue}
          loading={catalogueLoading}
          suppliers={suppliers.map((s) => ({ id: s.id, name: s.name, riskTier: s.riskTier }))}
          onChanged={reloadCatalogue}
          canWrite={canWrite}
        />
      )}

      {/* ── Monitoring tab ── */}
      {activeTab === "monitoring" && (
        <div>
          <p style={{ fontSize: 13, color: theme.color.subtle, marginBottom: 20 }}>
            Cross-supplier monitoring overview — scope changes, overdue reviews, outstanding assessments, and third parties awaiting questionnaire review.
          </p>

          {loading ? (
            <div style={{ ...card, color: theme.color.subtle, fontSize: 13 }}>Loading…</div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>

              {/* Questionnaires sent and not returned (lib/tprmChase.ts) */}
              <QuestionnaireChase
                rows={outstanding?.questionnaires ?? null}
                afterDays={outstanding?.afterDays ?? 14}
                autoChase={outstanding?.autoChase ?? null}
                onOpen={(id, questionnaireId) => { const s = suppliers.find((x) => x.id === id); if (s) openSupplier(s, "assessments", questionnaireId); }}              />

              {/* Automatic chasing and weekly public-record checks (GET / PATCH /tprm/settings) */}
              <SupplierAutomationCard canWrite={canWrite} onChanged={loadOutstanding} />

              {/* Supplier assurance across the register (docs/TPRM-SUPPLIER-ASSURANCE.md) */}
              <SharedFourthPartiesCard onOpen={(id) => { const s = suppliers.find((x) => x.id === id); if (s) openSupplier(s, "assurance"); }} />
              <SupplierGuestAccessCard onOpen={(id) => { const s = suppliers.find((x) => x.id === id); if (s) openSupplier(s, "assurance"); }} />
              <VendorRatingsCard canAdmin={canAdminIntegrations} />

              {/* Scope changes last 30 days (Gartner Insight 3) */}
              {recentScopeChanges.length > 0 ? (
                <div style={{ ...card }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: "var(--c-accentText)", marginBottom: 10, display: "flex", alignItems: "center", gap: 6 }}>
                    <GitBranch size={15} /> {recentScopeChanges.length} scope change{recentScopeChanges.length > 1 ? "s" : ""} in the last 30 days
                  </div>
                  {recentScopeChanges.map((sc) => (
                    <div key={sc.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", padding: "8px 0", borderBottom: "1px solid var(--c-border)" }}>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 600, color: theme.color.heading }}>{sc.supplierName}</div>
                        <div style={{ fontSize: 11, color: theme.color.muted }}>
                          {SCOPE_CHANGE_TYPES[sc.changeType] ?? sc.changeType} · {fmtDate(sc.changedAt)}
                        </div>
                        <div style={{ fontSize: 12, color: theme.color.subtle, marginTop: 2, lineHeight: 1.4 }}>{sc.description}</div>
                      </div>
                      <button onClick={() => openSupplier(sc.supplier)} style={{ ...btnPrimary, padding: "5px 12px", fontSize: 12, cursor: "pointer", flexShrink: 0, marginLeft: 12 }}>
                        View
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ ...card, display: "flex", gap: 12, alignItems: "center" }}>
                  <CheckCircle size={18} color="var(--c-successText)" />
                  <span style={{ fontSize: 13, color: theme.color.text }}>No scope changes logged in the last 30 days</span>
                </div>
              )}

              {/* Overdue reviews */}
              {(() => {
                const overdue = suppliers.filter((s) => s.nextReviewDate && isOverdue(s.nextReviewDate) && s.status === "active");
                return overdue.length === 0 ? (
                  <div style={{ ...card, display: "flex", gap: 12, alignItems: "center" }}>
                    <CheckCircle size={18} color="var(--c-successText)" />
                    <span style={{ fontSize: 13, color: theme.color.text }}>No review dates overdue</span>
                  </div>
                ) : (
                  <div style={{ ...card }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: "var(--c-dangerText)", marginBottom: 10, display: "flex", alignItems: "center", gap: 6 }}>
                      <AlertTriangle size={15} /> {overdue.length} supplier{overdue.length > 1 ? "s" : ""} with overdue review
                    </div>
                    {overdue.map((s) => (
                      <div key={s.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0", borderBottom: "1px solid var(--c-border)" }}>
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 600, color: theme.color.heading }}>{s.name}</div>
                          <div style={{ fontSize: 11, color: "var(--c-dangerText)" }}>
                            Due {fmtDateOnly(s.nextReviewDate)}{s.internalOwner ? ` · Owner: ${s.internalOwner}` : ""}
                          </div>
                        </div>
                        <button onClick={() => openSupplier(s, "assessments")} style={{ ...btnPrimary, padding: "5px 12px", fontSize: 12, cursor: "pointer" }}>
                          Assess Now
                        </button>
                      </div>
                    ))}
                  </div>
                );
              })()}

              {/* High/critical with no assessment */}
              {(() => {
                const atRisk = suppliers.filter((s) => (s.riskTier === "high" || s.riskTier === "critical") && s.questionnaires.length === 0 && s.status === "active");
                return atRisk.length === 0 ? (
                  <div style={{ ...card, display: "flex", gap: 12, alignItems: "center" }}>
                    <CheckCircle size={18} color="var(--c-successText)" />
                    <span style={{ fontSize: 13, color: theme.color.text }}>All high/critical suppliers have at least one assessment</span>
                  </div>
                ) : (
                  <div style={{ ...card }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: "var(--c-warningText)", marginBottom: 10, display: "flex", alignItems: "center", gap: 6 }}>
                      <AlertTriangle size={15} /> {atRisk.length} high/critical supplier{atRisk.length > 1 ? "s" : ""} without an assessment
                    </div>
                    {atRisk.map((s) => (
                      <div key={s.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0", borderBottom: "1px solid var(--c-border)" }}>
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 600, color: theme.color.heading }}>{s.name}</div>
                          <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 3 }}>
                            <Pill label={s.riskTier} colors={TIER_COLORS[s.riskTier] ?? { bg: "var(--c-faint)", text: "var(--c-muted)" }} />
                            {s.internalOwner && <span style={{ fontSize: 11, color: theme.color.muted }}>Owner: {s.internalOwner}</span>}
                          </div>
                        </div>
                        <button onClick={() => openSupplier(s, "assessments")} style={{ ...btnPrimary, padding: "5px 12px", fontSize: 12, cursor: "pointer" }}>
                          Send Assessment
                        </button>
                      </div>
                    ))}
                  </div>
                );
              })()}

              {/* Questionnaires awaiting review */}
              {(() => {
                const waiting = suppliers.flatMap((s) =>
                  s.questionnaires
                    .filter((q) => q.status === "returned")
                    .map((q) => ({ ...q, supplierName: s.name, supplier: s })),
                );
                return waiting.length === 0 ? (
                  <div style={{ ...card, display: "flex", gap: 12, alignItems: "center" }}>
                    <CheckCircle size={18} color="var(--c-successText)" />
                    <span style={{ fontSize: 13, color: theme.color.text }}>No questionnaires awaiting review</span>
                  </div>
                ) : (
                  <div style={{ ...card }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: "var(--c-accentText)", marginBottom: 10 }}>
                      {waiting.length} questionnaire{waiting.length > 1 ? "s" : ""} awaiting review
                    </div>
                    {waiting.map((q) => (
                      <div key={q.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0", borderBottom: "1px solid var(--c-border)" }}>
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 600, color: theme.color.heading }}>{q.supplierName}</div>
                          <div style={{ fontSize: 11, color: theme.color.muted }}>
                            {q.title} · {q.riskScore !== null ? `Risk score: ${q.riskScore}%` : "No score"} · Returned {fmtDate(q.returnedAt)}
                          </div>
                        </div>
                        <button onClick={() => openSupplier(q.supplier, "assessments", q.id)} style={{ ...btnPrimary, padding: "5px 12px", fontSize: 12, cursor: "pointer" }}>
                          Review
                        </button>
                      </div>
                    ))}
                  </div>
                );
              })()}

              {/* Low-confidence reviews needing follow-up (Gartner Insight 2) */}
              {(() => {
                const lowConf = suppliers.flatMap((s) =>
                  s.questionnaires
                    .filter((q) => q.status === "reviewed" && q.dataConfidence !== null && q.dataConfidence <= 2)
                    .map((q) => ({ ...q, supplierName: s.name, supplier: s }))
                );
                if (lowConf.length === 0) return null;
                return (
                  <div style={{ ...card }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: "var(--c-warningText)", marginBottom: 10, display: "flex", alignItems: "center", gap: 6 }}>
                      <AlertTriangle size={15} /> {lowConf.length} assessment{lowConf.length > 1 ? "s" : ""} with low data confidence — consider follow-up
                    </div>
                    {lowConf.map((q) => (
                      <div key={q.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0", borderBottom: "1px solid var(--c-border)" }}>
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 600, color: theme.color.heading }}>{q.supplierName}</div>
                          <div style={{ fontSize: 11, color: "var(--c-warningText)" }}>
                            Confidence rated {q.dataConfidence}/5 — on-site audit or independent verification recommended
                          </div>
                        </div>
                        <button onClick={() => openSupplier(q.supplier, "assessments", q.id)} style={{ ...btnPrimary, padding: "5px 12px", fontSize: 12, cursor: "pointer" }}>
                          View
                        </button>
                      </div>
                    ))}
                  </div>
                );
              })()}

            </div>
          )}
        </div>
      )}
    </div>
  );
}
